import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    DndContext,
    PointerSensor,
    KeyboardSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy,
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    GripVertical,
    LayoutDashboard,
    BarChart3,
    FolderOpen,
    Star,
    Activity,
    Megaphone,
    Settings as SettingsIcon,
    Mail,
    Handshake,
    Users,
    ExternalLink,
    Download,
    FileText,
    PlayCircle,
    ImagePlus,
    CalendarDays,
    Sparkles,
    GraduationCap,
    UsersRound,
} from "lucide-react";
import { getCloudinaryUrl } from "../../utils/cloudinary";
import { uploadFile, validateFile } from "../../utils/fileUpload";
import ReviewShareCard from "../../components/ReviewShareCard";
import ReviewShareModal from "../../components/ReviewShareModal";
import SummitAttendees from "./SummitAttendees";
import SpotlightAdmin from "./SpotlightAdmin";
import AcademyAdmin from "./Academy";
import InnerCircleAdmin from "./InnerCircle";
import './Admin.sass';

const ADMIN_NAV = [
    { key: 'overview', label: 'Overview', icon: LayoutDashboard },
    { key: 'collabs', label: 'Collab Analytics', icon: BarChart3 },
    { key: 'uploads', label: 'Collab Uploads', icon: FolderOpen },
    { key: 'reviewCards', label: 'Review Cards', icon: ImagePlus },
    { key: 'featured', label: 'Featured Photos', icon: Star },
    { key: 'spotlight', label: 'Spotlights', icon: Sparkles },
    { key: 'pulse', label: 'Live Pulse', icon: Activity },
    { key: 'broadcast', label: 'Broadcast', icon: Megaphone },
    { key: 'emails', label: 'Email Controls', icon: Mail },
    { key: 'events', label: 'Events', icon: CalendarDays },
    { key: 'academy', label: 'Academy', icon: GraduationCap },
    { key: 'innerCircle', label: 'Inner Circle', icon: UsersRound },
    { key: 'settings', label: 'Site Settings', icon: SettingsIcon },
];

const WEB_ANALYTICS_COLORS = ['#0f084b', '#dc1d08', '#2563eb', '#059669', '#f59e0b', '#7c3aed'];

const formatCompactNumber = (value) => {
    const number = Number(value || 0);
    if (number >= 1000000) return `${(number / 1000000).toFixed(1)}M`;
    if (number >= 1000) return `${(number / 1000).toFixed(1)}K`;
    return number.toLocaleString();
};

const formatDateTick = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatDeviceName = (name) => {
    if (!name) return 'Unknown';
    return String(name).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const shortenAnalyticsLabel = (label, maxLength = 34) => {
    const value = String(label || 'Unknown');
    return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
};

const CAROUSEL_IMAGE_OPTIONS = {
    width: 192,
    height: 192,
    crop: "fill",
    quality: "auto",
    format: "auto",
};

const normalizeHandle = (rawHandle) => {
    const value = String(rawHandle || "").trim();
    if (!value) return "";
    return value.startsWith("@") ? value : `@${value}`;
};

const optimizeImageUrl = (rawUrl) => {
    const imageUrl = String(rawUrl || "").trim();
    if (!imageUrl) return "";
    return getCloudinaryUrl(imageUrl, CAROUSEL_IMAGE_OPTIONS) || imageUrl;
};

const normalizeManagedProfile = (profile, index = 0) => {
    const sourceType = profile?.source_type || null;
    const sourceId = profile?.source_id ? String(profile.source_id) : null;
    const handle = normalizeHandle(profile?.handle);
    const imageUrl = String(profile?.image_url || "").trim();
    const localId = profile?.local_id || profile?.id || `${sourceType || "manual"}:${sourceId || handle}:${index}`;

    return {
        local_id: String(localId),
        source_type: sourceType,
        source_id: sourceId,
        handle,
        image_url: imageUrl,
        preview_image_url: optimizeImageUrl(imageUrl),
        full_name: profile?.full_name || null,
        brand_name: profile?.brand_name || null,
    };
};

const SortableCarouselProfileRow = ({ id, profile, index, onRemove }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
    };

    return (
        <div ref={setNodeRef} style={style} className="carousel-profile-row">
            <button
                type="button"
                className="carousel-drag-handle"
                aria-label={`Drag to reorder ${profile.handle}`}
                {...attributes}
                {...listeners}
            >
                <GripVertical size={16} />
            </button>
            <div className="carousel-profile-rank">{index + 1}</div>
            <img
                src={profile.preview_image_url || profile.image_url}
                alt={profile.handle}
                className="carousel-profile-avatar"
                loading="lazy"
            />
            <div className="carousel-profile-meta">
                <div className="carousel-profile-handle">{profile.handle}</div>
                <div className="carousel-profile-subtext">
                    {profile.full_name || profile.brand_name || profile.source_type || "profile"}
                </div>
            </div>
            <button
                type="button"
                className="carousel-remove-btn"
                onClick={() => onRemove(profile.local_id)}
            >
                Remove
            </button>
        </div>
    );
};

const Admin = () => {
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [hostCount, setHostCount] = useState(null);
    const [hostSubscriptions, setHostSubscriptions] = useState([]);
    const [creatorCount, setCreatorCount] = useState(null);
    const [creatorSubscriptions, setCreatorSubscriptions] = useState([]);
    const [travelerCount, setTravelerCount] = useState(null);
    const [travelerSubscriptions, setTravelerSubscriptions] = useState([]);
    const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
    const [platformStats, setPlatformStats] = useState(null);
    const [contentMetrics, setContentMetrics] = useState(null);
    const [engagementMetrics, setEngagementMetrics] = useState(null);
    const [loadingMetrics, setLoadingMetrics] = useState(false);
    const [vercelAnalytics, setVercelAnalytics] = useState(null);
    const [loadingVercelAnalytics, setLoadingVercelAnalytics] = useState(false);
    const [vercelAnalyticsError, setVercelAnalyticsError] = useState("");
    const [vercelDatePreset, setVercelDatePreset] = useState("30d");
    const [vercelCustomStart, setVercelCustomStart] = useState(() => new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
    const [vercelCustomEnd, setVercelCustomEnd] = useState(() => new Date().toISOString().slice(0, 10));
    const [vercelGroupBy, setVercelGroupBy] = useState("day");
    const [abandonedCartCount, setAbandonedCartCount] = useState(null);
    const [abandonedCartUsers, setAbandonedCartUsers] = useState([]);
    const [loadingAbandonedCart, setLoadingAbandonedCart] = useState(false);

    // Paid hosts hidden from the public /hosts page (no property or no photo)
    const [hiddenHosts, setHiddenHosts] = useState([]);
    const [hiddenHostsTotalPaying, setHiddenHostsTotalPaying] = useState(null);
    const [loadingHiddenHosts, setLoadingHiddenHosts] = useState(false);

    // Paying creators missing a profile pic or IG handle
    const [hiddenCreators, setHiddenCreators] = useState([]);
    const [hiddenCreatorsTotalPaying, setHiddenCreatorsTotalPaying] = useState(null);
    const [loadingHiddenCreators, setLoadingHiddenCreators] = useState(false);

    // Which user a nudge email is currently being sent to (disables the button)
    const [nudgingUserId, setNudgingUserId] = useState(null);

    // Pulse Manager state
    const [pulseMessages, setPulseMessages] = useState([]);
    const [loadingPulseMessages, setLoadingPulseMessages] = useState(false);
    const [pulseActivity, setPulseActivity] = useState([]);
    const [loadingPulseActivity, setLoadingPulseActivity] = useState(false);
    const [showHiddenActivity, setShowHiddenActivity] = useState(false);
    const [newPulseMessage, setNewPulseMessage] = useState("");
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editingMessageText, setEditingMessageText] = useState("");
    const [editingActivityId, setEditingActivityId] = useState(null);
    const [editingActivityCopy, setEditingActivityCopy] = useState("");
    const [pulseError, setPulseError] = useState("");

    // Checkout carousel manager state
    const [checkoutCarouselProfiles, setCheckoutCarouselProfiles] = useState([]);
    const [loadingCheckoutCarousel, setLoadingCheckoutCarousel] = useState(false);
    const [savingCheckoutCarousel, setSavingCheckoutCarousel] = useState(false);
    const [checkoutCarouselSearch, setCheckoutCarouselSearch] = useState("");
    const [checkoutCarouselSearchResults, setCheckoutCarouselSearchResults] = useState([]);
    const [loadingCheckoutCarouselSearch, setLoadingCheckoutCarouselSearch] = useState(false);
    const [checkoutCarouselError, setCheckoutCarouselError] = useState("");
    const [checkoutCarouselSuccess, setCheckoutCarouselSuccess] = useState("");

    // Social / OpenGraph share image manager state
    const [siteOgImageUrl, setSiteOgImageUrl] = useState(null);
    const [siteOgPreviewUrl, setSiteOgPreviewUrl] = useState(null);
    const [loadingSiteMeta, setLoadingSiteMeta] = useState(false);
    const [uploadingSiteOg, setUploadingSiteOg] = useState(false);
    const [savingSiteOg, setSavingSiteOg] = useState(false);
    const [siteMetaError, setSiteMetaError] = useState("");
    const [siteMetaSuccess, setSiteMetaSuccess] = useState("");
    const siteOgInputRef = React.useRef(null);

    // Email controls (global kill switches) state
    const [emailFlags, setEmailFlags] = useState([]);
    const [essentialEmails, setEssentialEmails] = useState([]);
    const [loadingEmailFlags, setLoadingEmailFlags] = useState(false);
    const [emailFlagsError, setEmailFlagsError] = useState("");
    const [savingEmailFlagKey, setSavingEmailFlagKey] = useState(null);

    // Broadcast message state
    const [broadcastDraft, setBroadcastDraft] = useState("");
    const [broadcastSending, setBroadcastSending] = useState(false);
    const [broadcastResult, setBroadcastResult] = useState(null);
    const [broadcastError, setBroadcastError] = useState("");
    const [broadcastConfirming, setBroadcastConfirming] = useState(false);

    // Collab Analytics state
    const [collabAnalytics, setCollabAnalytics] = useState(null);
    const [loadingCollabAnalytics, setLoadingCollabAnalytics] = useState(false);
    const [collabAnalyticsError, setCollabAnalyticsError] = useState("");
    const [collabDatePreset, setCollabDatePreset] = useState("30d");
    const [collabGroupBy, setCollabGroupBy] = useState("day");
    const [collabStatusFilter, setCollabStatusFilter] = useState("all");
    const [collabSearch, setCollabSearch] = useState("");
    const [collabPage, setCollabPage] = useState(0);
    const [expandedCollabId, setExpandedCollabId] = useState(null);
    const [collabSortField, setCollabSortField] = useState("createdAt");
    const [collabSortDir, setCollabSortDir] = useState("desc");

    // Featured Submissions state
    const [featuredSubmissions, setFeaturedSubmissions] = useState([]);
    const [featuredCounts, setFeaturedCounts] = useState({ pending: 0, approved: 0, featured: 0, declined: 0, total: 0 });
    const [featuredFilter, setFeaturedFilter] = useState('all');
    const [featuredLoading, setFeaturedLoading] = useState(false);
    const [featuredNoteId, setFeaturedNoteId] = useState(null);
    const [featuredNoteText, setFeaturedNoteText] = useState('');

    // Dashboard navigation (supports deep links like /admin?section=spotlight)
    const [activeSection, setActiveSection] = useState(() => {
        try {
            const requested = new URLSearchParams(window.location.search).get('section');
            return requested && ADMIN_NAV.some((item) => item.key === requested) ? requested : 'overview';
        } catch (_) {
            return 'overview';
        }
    });

    // Collab Uploads state
    const [collabUploads, setCollabUploads] = useState([]);
    const [collabUploadsSummary, setCollabUploadsSummary] = useState({ files: 0, links: 0 });
    const [collabUploadsKind, setCollabUploadsKind] = useState('file');
    const [collabUploadsSearch, setCollabUploadsSearch] = useState('');
    const [loadingCollabUploads, setLoadingCollabUploads] = useState(false);
    const [collabUploadsError, setCollabUploadsError] = useState('');

    // Review Cards state
    const [reviewCards, setReviewCards] = useState([]);
    const [reviewCardsSummary, setReviewCardsSummary] = useState({ total: 0, pending: 0, accepted: 0, missingPhoto: 0 });
    const [reviewCardsFilter, setReviewCardsFilter] = useState('all'); // 'all' | 'pending' | 'accepted' | 'noPhoto'
    const [reviewCardsSearch, setReviewCardsSearch] = useState('');
    const [loadingReviewCards, setLoadingReviewCards] = useState(false);
    const [reviewCardsError, setReviewCardsError] = useState('');
    const [shareCardReview, setShareCardReview] = useState(null);

    const carouselSensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Add noindex meta tags to prevent search engine indexing
    useEffect(() => {
        // Add robots noindex meta tag
        const robotsMeta = document.createElement('meta');
        robotsMeta.name = 'robots';
        robotsMeta.content = 'noindex, nofollow';
        document.head.appendChild(robotsMeta);

        // Add Google-specific noindex
        const googleMeta = document.createElement('meta');
        googleMeta.name = 'googlebot';
        googleMeta.content = 'noindex, nofollow';
        document.head.appendChild(googleMeta);

        // Cleanup function to remove meta tags when component unmounts
        return () => {
            const robotsTag = document.querySelector('meta[name="robots"][content="noindex, nofollow"]');
            const googleTag = document.querySelector('meta[name="googlebot"][content="noindex, nofollow"]');
            if (robotsTag) robotsTag.remove();
            if (googleTag) googleTag.remove();
        };
    }, []);

    useEffect(() => {
        // Check if user is logged in
        try {
            const storedEmail = localStorage.getItem('postaway_email');
            if (!storedEmail) {
                // Not logged in - redirect to login
                navigate('/creator-login');
                return;
            }

            // Check if admin password is already verified in this session
            const adminVerified = sessionStorage.getItem('admin_password_verified');
            if (adminVerified === 'true') {
                setIsAuthenticated(true);
                // Load all data
                fetchAllSubscriptions();
                fetchAllMetrics();
                fetchAbandonedCartUsers();
                fetchHiddenHosts();
                fetchHiddenCreators();
                fetchCheckoutCarouselProfiles();
                fetchSiteMeta();
                fetchPulseMessages();
                fetchPulseActivity(false);
                fetchCollabAnalytics();
                fetchFeaturedSubmissions();
                fetchEmailFlags();
            }
        } catch (err) {
            console.error('Error checking auth:', err);
            navigate('/creator-login');
        } finally {
            setIsLoading(false);
        }
    }, [navigate]);

    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        setError("");

        if (password === "Unicorn2025!") {
            // Store verification in sessionStorage
            sessionStorage.setItem('admin_password_verified', 'true');
            setIsAuthenticated(true);
            // Load all data
            fetchAllSubscriptions();
            fetchAllMetrics();
            fetchAbandonedCartUsers();
            fetchHiddenHosts();
            fetchHiddenCreators();
            fetchCheckoutCarouselProfiles();
            fetchSiteMeta();
            fetchPulseMessages();
            fetchPulseActivity(false);
            fetchCollabAnalytics();
            fetchFeaturedSubmissions();
            fetchEmailFlags();
        } else {
            setError("Incorrect password. Please try again.");
            setPassword("");
        }
    };

    const fetchAllSubscriptions = async () => {
        setLoadingSubscriptions(true);
        try {
            // Fetch all three subscription types in parallel
            const [hostRes, creatorRes, travelerRes] = await Promise.all([
                fetch('/api/admin/host-subscriptions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ adminPassword: 'Unicorn2025!' })
                }),
                fetch('/api/admin/creator-subscriptions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ adminPassword: 'Unicorn2025!' })
                }),
                fetch('/api/admin/traveler-subscriptions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ adminPassword: 'Unicorn2025!' })
                })
            ]);

            const hostData = await hostRes.json();
            const creatorData = await creatorRes.json();
            const travelerData = await travelerRes.json();

            if (hostData.ok) {
                setHostCount(hostData.count);
                setHostSubscriptions(hostData.subscriptions || []);
            } else {
                console.error('Failed to fetch host subscriptions:', hostData.error);
            }

            if (creatorData.ok) {
                setCreatorCount(creatorData.count);
                setCreatorSubscriptions(creatorData.subscriptions || []);
            } else {
                console.error('Failed to fetch creator subscriptions:', creatorData.error);
            }

            if (travelerData.ok) {
                setTravelerCount(travelerData.count);
                setTravelerSubscriptions(travelerData.subscriptions || []);
            } else {
                console.error('Failed to fetch traveler subscriptions:', travelerData.error);
            }
        } catch (err) {
            console.error('Error fetching subscriptions:', err);
        } finally {
            setLoadingSubscriptions(false);
        }
    };

    const fetchHiddenHosts = async () => {
        setLoadingHiddenHosts(true);
        try {
            const res = await fetch('/api/admin/hidden-hosts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: 'Unicorn2025!' })
            });
            const data = await res.json();
            if (data.ok) {
                setHiddenHosts(data.hiddenHosts || []);
                setHiddenHostsTotalPaying(data.totalPayingHosts ?? null);
            } else {
                console.error('Failed to fetch hidden hosts:', data.error);
            }
        } catch (err) {
            console.error('Error fetching hidden hosts:', err);
        } finally {
            setLoadingHiddenHosts(false);
        }
    };

    const fetchHiddenCreators = async () => {
        setLoadingHiddenCreators(true);
        try {
            const res = await fetch('/api/admin/hidden-creators', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: 'Unicorn2025!' })
            });
            const data = await res.json();
            if (data.ok) {
                setHiddenCreators(data.creators || []);
                setHiddenCreatorsTotalPaying(data.totalPayingCreators ?? null);
            } else {
                console.error('Failed to fetch hidden creators:', data.error);
            }
        } catch (err) {
            console.error('Error fetching hidden creators:', err);
        } finally {
            setLoadingHiddenCreators(false);
        }
    };

    // One-click nudge: emails the user a reminder to finish their profile and
    // stamps lastNudgedAt so admins can see who was already contacted.
    const sendNudge = async (kind, target) => {
        if (nudgingUserId) return;
        setNudgingUserId(target.userId);
        try {
            const endpoint = kind === 'host' ? '/api/admin/hidden-hosts' : '/api/admin/hidden-creators';
            const reasons = kind === 'host'
                ? [target.reason === 'No property added'
                    ? 'Add your property so it can appear on the hosts page.'
                    : 'Add a property photo so your listing can appear on the hosts page.']
                : (target.reasons || []).map(r => r === 'Missing profile photo'
                    ? 'Add a profile photo so you appear on the creators page.'
                    : 'Add your Instagram handle so hosts can vet your content.');
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminPassword: 'Unicorn2025!',
                    action: 'nudge',
                    userId: target.userId,
                    email: target.email,
                    firstName: target.firstName,
                    reasons,
                })
            });
            const data = await res.json();
            if (data.ok) {
                const stamp = data.sentAt || new Date().toISOString();
                if (kind === 'host') {
                    setHiddenHosts(prev => prev.map(h => h.userId === target.userId ? { ...h, lastNudgedAt: stamp } : h));
                } else {
                    setHiddenCreators(prev => prev.map(c => c.userId === target.userId ? { ...c, lastNudgedAt: stamp } : c));
                }
            } else {
                alert(`Nudge failed: ${data.error || 'unknown error'}`);
            }
        } catch (err) {
            console.error('Error sending nudge:', err);
            alert('Nudge failed. Please try again.');
        } finally {
            setNudgingUserId(null);
        }
    };

    const exportHiddenCreatorsToCSV = () => {
        if (!hiddenCreators || hiddenCreators.length === 0) {
            alert('No data to export');
            return;
        }
        const headers = ['Name/Handle', 'Email', 'Missing', 'Profile Pic', 'IG Handle', 'Plan', 'Status', 'Subscribed', 'Last Nudged'];
        const rows = hiddenCreators.map(c => [
            c.displayName || (c.handle ? `@${c.handle}` : c.firstName || ''),
            c.email || '',
            (c.reasons || []).join('; '),
            c.hasAvatar ? 'yes' : 'no',
            c.hasInstagram ? 'yes' : 'no',
            c.planName || '',
            c.subscriptionStatus || '',
            c.subscribedAt ? new Date(c.subscribedAt).toLocaleDateString() : '',
            c.lastNudgedAt ? new Date(c.lastNudgedAt).toLocaleDateString() : ''
        ]);
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `incomplete-paid-creators-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportHiddenHostsToCSV = () => {
        if (!hiddenHosts || hiddenHosts.length === 0) {
            alert('No data to export');
            return;
        }
        const headers = ['Brand/Handle', 'Email', 'First Name', 'Reason Hidden', 'Has Profile Pic', 'Properties', 'Plan', 'Status', 'Subscribed', 'Last Nudged'];
        const rows = hiddenHosts.map(h => [
            h.brandName || (h.handle ? `@${h.handle}` : ''),
            h.email || '',
            h.firstName || '',
            h.reason || '',
            h.hasAvatar ? 'yes' : 'no',
            h.propertyCount ?? '',
            h.planName || '',
            h.subscriptionStatus || '',
            h.subscribedAt ? new Date(h.subscribedAt).toLocaleDateString() : '',
            h.lastNudgedAt ? new Date(h.lastNudgedAt).toLocaleDateString() : ''
        ]);
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `hidden-paid-hosts-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getVercelDateRange = useCallback((preset) => {
        if (preset === 'custom' && vercelCustomStart && vercelCustomEnd) {
            return {
                start: new Date(`${vercelCustomStart}T00:00:00`).toISOString(),
                end: new Date(`${vercelCustomEnd}T23:59:59`).toISOString(),
            };
        }
        const now = new Date();
        const end = now.toISOString();
        let days = 30;
        if (preset === '7d') days = 7;
        if (preset === '90d') days = 90;
        const start = new Date(Date.now() - days * 86400000).toISOString();
        return { start, end };
    }, [vercelCustomStart, vercelCustomEnd]);

    const fetchVercelAnalytics = useCallback(async () => {
        setLoadingVercelAnalytics(true);
        setVercelAnalyticsError("");
        try {
            const { start, end } = getVercelDateRange(vercelDatePreset);
            const response = await fetch('/api/admin/vercel-analytics', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminPassword: 'Unicorn2025!',
                    startDate: start,
                    endDate: end,
                    groupBy: vercelGroupBy,
                })
            });
            const data = await response.json();
            if (data.ok) {
                setVercelAnalytics(data.analytics || null);
            } else {
                setVercelAnalyticsError(data.error || 'Failed to load Vercel analytics');
            }
        } catch (err) {
            console.error('Error fetching Vercel analytics:', err);
            setVercelAnalyticsError('Failed to load Vercel analytics');
        } finally {
            setLoadingVercelAnalytics(false);
        }
    }, [getVercelDateRange, vercelDatePreset, vercelGroupBy]);

    useEffect(() => {
        if (!isAuthenticated) return;
        fetchVercelAnalytics();
    }, [isAuthenticated, fetchVercelAnalytics]);

    const fetchAllMetrics = async () => {
        setLoadingMetrics(true);
        try {
            const [platformRes, contentRes, engagementRes] = await Promise.all([
                fetch('/api/admin/platform-stats', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ adminPassword: 'Unicorn2025!' })
                }),
                fetch('/api/admin/content-metrics', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ adminPassword: 'Unicorn2025!' })
                }),
                fetch('/api/admin/engagement-metrics', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ adminPassword: 'Unicorn2025!' })
                })
            ]);

            const platformData = await platformRes.json();
            const contentData = await contentRes.json();
            const engagementData = await engagementRes.json();

            if (platformData.ok) {
                setPlatformStats(platformData.stats);
            } else {
                console.error('Failed to fetch platform stats:', platformData.error);
            }

            if (contentData.ok) {
                setContentMetrics(contentData.metrics);
            } else {
                console.error('Failed to fetch content metrics:', contentData.error);
            }

            if (engagementData.ok) {
                setEngagementMetrics(engagementData.metrics);
            } else {
                console.error('Failed to fetch engagement metrics:', engagementData.error);
            }
        } catch (err) {
            console.error('Error fetching metrics:', err);
        } finally {
            setLoadingMetrics(false);
        }
    };

    const fetchAbandonedCartUsers = async () => {
        setLoadingAbandonedCart(true);
        try {
            const response = await fetch('/api/admin/abandoned-cart-users', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: 'Unicorn2025!' })
            });
            const data = await response.json();
            if (data.ok) {
                setAbandonedCartCount(data.count);
                setAbandonedCartUsers(data.users || []);
            } else {
                console.error('Failed to fetch abandoned cart users:', data.error);
            }
        } catch (err) {
            console.error('Error fetching abandoned cart users:', err);
        } finally {
            setLoadingAbandonedCart(false);
        }
    };

    const fetchEmailFlags = async () => {
        setLoadingEmailFlags(true);
        setEmailFlagsError("");
        try {
            const response = await fetch('/api/admin/email-flags', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: 'Unicorn2025!', action: 'list' })
            });
            const data = await response.json();
            if (data.ok) {
                setEmailFlags(data.flags || []);
                setEssentialEmails(data.essential || []);
            } else {
                setEmailFlagsError(`Failed to load email controls: ${data.error || 'unknown_error'}`);
            }
        } catch (err) {
            console.error('Error fetching email flags:', err);
            setEmailFlagsError('Failed to load email controls.');
        } finally {
            setLoadingEmailFlags(false);
        }
    };

    const handleToggleEmailFlag = async (key, enabled) => {
        setSavingEmailFlagKey(key);
        setEmailFlagsError("");
        // Optimistic UI update; reverted on failure via re-fetch of server state.
        setEmailFlags(prev => prev.map(flag => flag.key === key ? { ...flag, enabled } : flag));
        try {
            const response = await fetch('/api/admin/email-flags', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: 'Unicorn2025!', action: 'set', key, enabled })
            });
            const data = await response.json();
            if (data.ok) {
                setEmailFlags(data.flags || []);
            } else {
                setEmailFlagsError(`Failed to update: ${data.error || 'unknown_error'}`);
                await fetchEmailFlags();
            }
        } catch (err) {
            console.error('Error updating email flag:', err);
            setEmailFlagsError('Failed to update email setting.');
            await fetchEmailFlags();
        } finally {
            setSavingEmailFlagKey(null);
        }
    };

    const fetchSiteMeta = async () => {
        setLoadingSiteMeta(true);
        setSiteMetaError("");
        try {
            const response = await fetch('/api/admin/site-meta', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: 'Unicorn2025!', action: 'list' })
            });
            const data = await response.json();
            if (data.ok) {
                setSiteOgImageUrl(data.ogImageUrl || null);
            } else {
                setSiteMetaError(`Failed to load share image: ${data.error || 'unknown_error'}`);
            }
        } catch (err) {
            console.error('Error fetching site meta:', err);
            setSiteMetaError('Failed to load social share image settings.');
        } finally {
            setLoadingSiteMeta(false);
        }
    };

    const handleSiteOgFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (siteOgInputRef.current) siteOgInputRef.current.value = "";
        if (!file) return;

        const validation = validateFile(file, {
            allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "image/heic", "image/heif"],
            maxSize: 10 * 1024 * 1024,
        });
        if (!validation.isValid) {
            setSiteMetaError(validation.error || "Invalid file.");
            return;
        }

        setSiteMetaError("");
        setSiteMetaSuccess("");
        setUploadingSiteOg(true);
        let localPreview = null;
        try {
            localPreview = URL.createObjectURL(file);
            setSiteOgPreviewUrl(localPreview);
        } catch (_) {}

        try {
            const url = await uploadFile(file);
            setSiteOgImageUrl(url);
            setSiteOgPreviewUrl(null);
            // Persist immediately so the new image is live for shared links.
            await handleSaveSiteOg(url);
        } catch (err) {
            console.error('Site OG upload failed:', err);
            setSiteMetaError(err?.message || "Upload failed.");
            setSiteOgPreviewUrl(null);
        } finally {
            setUploadingSiteOg(false);
            if (localPreview) {
                try { URL.revokeObjectURL(localPreview); } catch (_) {}
            }
        }
    };

    const handleSaveSiteOg = async (urlOverride) => {
        const ogImageUrl = (urlOverride || siteOgImageUrl || "").trim();
        if (!ogImageUrl) {
            setSiteMetaError("Upload an image first.");
            return;
        }
        setSavingSiteOg(true);
        setSiteMetaError("");
        setSiteMetaSuccess("");
        try {
            const response = await fetch('/api/admin/site-meta', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: 'Unicorn2025!', action: 'save', ogImageUrl })
            });
            const data = await response.json();
            if (data.ok) {
                setSiteOgImageUrl(data.ogImageUrl || ogImageUrl);
                setSiteMetaSuccess("Social share image saved. New links will use this photo.");
            } else {
                setSiteMetaError(`Failed to save: ${data.error || 'unknown_error'}`);
            }
        } catch (err) {
            console.error('Error saving site OG image:', err);
            setSiteMetaError('Failed to save social share image.');
        } finally {
            setSavingSiteOg(false);
        }
    };

    const handleResetSiteOg = async () => {
        if (!window.confirm('Reset the social share image to the platform default?')) return;
        setSavingSiteOg(true);
        setSiteMetaError("");
        setSiteMetaSuccess("");
        try {
            const response = await fetch('/api/admin/site-meta', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: 'Unicorn2025!', action: 'reset' })
            });
            const data = await response.json();
            if (data.ok) {
                setSiteOgImageUrl(null);
                setSiteMetaSuccess("Reset to the default share image.");
            } else {
                setSiteMetaError(`Failed to reset: ${data.error || 'unknown_error'}`);
            }
        } catch (err) {
            console.error('Error resetting site OG image:', err);
            setSiteMetaError('Failed to reset social share image.');
        } finally {
            setSavingSiteOg(false);
        }
    };

    const fetchCheckoutCarouselProfiles = async () => {
        setLoadingCheckoutCarousel(true);
        setCheckoutCarouselError("");

        try {
            const response = await fetch('/api/admin/checkout-carousel', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminPassword: 'Unicorn2025!',
                    action: 'list',
                }),
            });

            const data = await response.json();
            if (!data.ok) {
                setCheckoutCarouselError(`Failed to load carousel profiles: ${data.error || 'unknown_error'}`);
                return;
            }

            const normalized = (data.profiles || [])
                .map((profile, index) => normalizeManagedProfile(profile, index))
                .filter((profile) => profile.handle && profile.image_url);

            setCheckoutCarouselProfiles(normalized);
        } catch (err) {
            console.error('Error fetching checkout carousel profiles:', err);
            setCheckoutCarouselError('Failed to load checkout carousel profiles.');
        } finally {
            setLoadingCheckoutCarousel(false);
        }
    };

    const searchCheckoutCarouselProfiles = async (query) => {
        const normalizedQuery = String(query || '').trim();
        if (normalizedQuery.length < 2) {
            setCheckoutCarouselSearchResults([]);
            return;
        }

        setLoadingCheckoutCarouselSearch(true);
        setCheckoutCarouselError("");

        try {
            const response = await fetch('/api/admin/checkout-carousel', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminPassword: 'Unicorn2025!',
                    action: 'search',
                    query: normalizedQuery,
                }),
            });

            const data = await response.json();
            if (!data.ok) {
                setCheckoutCarouselError(`Search failed: ${data.error || 'unknown_error'}`);
                setCheckoutCarouselSearchResults([]);
                return;
            }

            const normalized = (data.results || [])
                .map((profile, index) => normalizeManagedProfile(profile, index))
                .filter((profile) => profile.handle && profile.image_url);

            setCheckoutCarouselSearchResults(normalized);
        } catch (err) {
            console.error('Error searching checkout carousel profiles:', err);
            setCheckoutCarouselError('Failed to search profiles.');
            setCheckoutCarouselSearchResults([]);
        } finally {
            setLoadingCheckoutCarouselSearch(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) return;
        const query = checkoutCarouselSearch.trim();
        if (!query) {
            setCheckoutCarouselSearchResults([]);
            return;
        }

        const debounceId = setTimeout(() => {
            searchCheckoutCarouselProfiles(query);
        }, 250);

        return () => clearTimeout(debounceId);
    }, [checkoutCarouselSearch, isAuthenticated]);

    useEffect(() => {
        if (!checkoutCarouselSuccess) return;
        const timeoutId = setTimeout(() => setCheckoutCarouselSuccess(""), 3000);
        return () => clearTimeout(timeoutId);
    }, [checkoutCarouselSuccess]);

    const isAlreadyInCheckoutCarousel = (candidate) => {
        const candidateKey = `${candidate.source_type || 'manual'}:${candidate.source_id || candidate.handle}`;
        return checkoutCarouselProfiles.some((profile) => {
            const profileKey = `${profile.source_type || 'manual'}:${profile.source_id || profile.handle}`;
            return profileKey === candidateKey;
        });
    };

    const handleAddCheckoutCarouselProfile = (candidate) => {
        if (!candidate) return;
        if (isAlreadyInCheckoutCarousel(candidate)) return;
        setCheckoutCarouselProfiles((prev) => [
            ...prev,
            normalizeManagedProfile(candidate, prev.length),
        ]);
        setCheckoutCarouselSuccess("");
    };

    const handleRemoveCheckoutCarouselProfile = (localId) => {
        setCheckoutCarouselProfiles((prev) =>
            prev.filter((profile) => profile.local_id !== localId)
        );
        setCheckoutCarouselSuccess("");
    };

    const handleCheckoutCarouselDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;

        setCheckoutCarouselProfiles((prev) => {
            const oldIndex = prev.findIndex((profile) => profile.local_id === active.id);
            const newIndex = prev.findIndex((profile) => profile.local_id === over.id);
            if (oldIndex < 0 || newIndex < 0) return prev;
            return arrayMove(prev, oldIndex, newIndex);
        });
        setCheckoutCarouselSuccess("");
    };

    const handleSaveCheckoutCarousel = async () => {
        setSavingCheckoutCarousel(true);
        setCheckoutCarouselError("");
        setCheckoutCarouselSuccess("");

        try {
            const response = await fetch('/api/admin/checkout-carousel', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminPassword: 'Unicorn2025!',
                    action: 'save',
                    profiles: checkoutCarouselProfiles.map((profile) => ({
                        source_type: profile.source_type,
                        source_id: profile.source_id,
                        handle: profile.handle,
                        image_url: profile.image_url,
                    })),
                }),
            });

            const data = await response.json();
            if (!data.ok) {
                setCheckoutCarouselError(`Failed to save: ${data.error || 'unknown_error'}`);
                return;
            }

            const normalized = (data.profiles || [])
                .map((profile, index) => normalizeManagedProfile(profile, index))
                .filter((profile) => profile.handle && profile.image_url);

            setCheckoutCarouselProfiles(normalized);
            setCheckoutCarouselSuccess("Checkout carousel saved.");
        } catch (err) {
            console.error('Error saving checkout carousel:', err);
            setCheckoutCarouselError('Failed to save checkout carousel.');
        } finally {
            setSavingCheckoutCarousel(false);
        }
    };

    // Pulse Messages CRUD
    const fetchPulseMessages = async () => {
        setLoadingPulseMessages(true);
        setPulseError("");
        try {
            const response = await fetch('/api/admin/pulse-messages', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: 'Unicorn2025!', action: 'list' })
            });
            const data = await response.json();
            if (data.ok) {
                setPulseMessages(data.messages || []);
            } else {
                setPulseError('Failed to load pulse messages: ' + (data.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error fetching pulse messages:', err);
            setPulseError('Failed to load pulse messages');
        } finally {
            setLoadingPulseMessages(false);
        }
    };

    const handleCreatePulseMessage = async () => {
        if (!newPulseMessage.trim()) return;
        setPulseError("");
        try {
            const response = await fetch('/api/admin/pulse-messages', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    adminPassword: 'Unicorn2025!', 
                    action: 'create',
                    message: newPulseMessage.trim()
                })
            });
            const data = await response.json();
            if (data.ok) {
                setNewPulseMessage("");
                fetchPulseMessages();
            } else {
                setPulseError('Failed to create message: ' + (data.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error creating pulse message:', err);
            setPulseError('Failed to create message');
        }
    };

    const handleUpdatePulseMessage = async (id) => {
        if (!editingMessageText.trim()) return;
        setPulseError("");
        try {
            const response = await fetch('/api/admin/pulse-messages', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    adminPassword: 'Unicorn2025!', 
                    action: 'update',
                    id,
                    message: editingMessageText.trim()
                })
            });
            const data = await response.json();
            if (data.ok) {
                setEditingMessageId(null);
                setEditingMessageText("");
                fetchPulseMessages();
            } else {
                setPulseError('Failed to update message: ' + (data.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error updating pulse message:', err);
            setPulseError('Failed to update message');
        }
    };

    const handleDeletePulseMessage = async (id) => {
        if (!window.confirm('Are you sure you want to delete this pulse message?')) return;
        setPulseError("");
        try {
            const response = await fetch('/api/admin/pulse-messages', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    adminPassword: 'Unicorn2025!', 
                    action: 'delete',
                    id
                })
            });
            const data = await response.json();
            if (data.ok) {
                fetchPulseMessages();
            } else {
                setPulseError('Failed to delete message: ' + (data.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error deleting pulse message:', err);
            setPulseError('Failed to delete message');
        }
    };

    const handleTogglePulseMessageEnabled = async (id, currentEnabled) => {
        setPulseError("");
        try {
            const response = await fetch('/api/admin/pulse-messages', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    adminPassword: 'Unicorn2025!', 
                    action: 'update',
                    id,
                    is_enabled: !currentEnabled
                })
            });
            const data = await response.json();
            if (data.ok) {
                fetchPulseMessages();
            } else {
                setPulseError('Failed to toggle message: ' + (data.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error toggling pulse message:', err);
            setPulseError('Failed to toggle message');
        }
    };

    // Pulse Activity CRUD
    const fetchPulseActivity = async (includeHidden) => {
        setLoadingPulseActivity(true);
        setPulseError("");
        try {
            const response = await fetch('/api/admin/pulse-activity', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    adminPassword: 'Unicorn2025!', 
                    action: 'list',
                    include_hidden: includeHidden,
                    limit: 50
                })
            });
            const data = await response.json();
            if (data.ok) {
                setPulseActivity(data.events || []);
            } else {
                setPulseError('Failed to load activity: ' + (data.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error fetching pulse activity:', err);
            setPulseError('Failed to load activity');
        } finally {
            setLoadingPulseActivity(false);
        }
    };

    const handleUpdateActivityCopy = async (id) => {
        setPulseError("");
        try {
            const response = await fetch('/api/admin/pulse-activity', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    adminPassword: 'Unicorn2025!', 
                    action: 'update',
                    id,
                    admin_copy: editingActivityCopy.trim() || null
                })
            });
            const data = await response.json();
            if (data.ok) {
                setEditingActivityId(null);
                setEditingActivityCopy("");
                fetchPulseActivity(showHiddenActivity);
            } else {
                setPulseError('Failed to update activity: ' + (data.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error updating activity:', err);
            setPulseError('Failed to update activity');
        }
    };

    const handleToggleActivityHidden = async (id, currentHidden) => {
        setPulseError("");
        try {
            const response = await fetch('/api/admin/pulse-activity', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    adminPassword: 'Unicorn2025!', 
                    action: currentHidden ? 'unhide' : 'hide',
                    id
                })
            });
            const data = await response.json();
            if (data.ok) {
                fetchPulseActivity(showHiddenActivity);
            } else {
                setPulseError('Failed to toggle visibility: ' + (data.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error toggling activity visibility:', err);
            setPulseError('Failed to toggle visibility');
        }
    };

    const handleSendBroadcast = async () => {
        if (!broadcastDraft.trim()) return;
        const message = broadcastDraft.trim();
        setBroadcastSending(true);
        setBroadcastError("");
        setBroadcastResult(null);
        try {
            const response = await fetch('/api/admin/broadcast-message', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminPassword: 'Unicorn2025!',
                    message
                })
            });
            const data = await response.json();
            if (data.ok) {
                setBroadcastResult({ ...data, message });
                setBroadcastDraft("");
                setBroadcastConfirming(false);
            } else {
                setBroadcastError(data.error || 'Broadcast failed');
            }
        } catch (err) {
            console.error('Error sending broadcast:', err);
            setBroadcastError('Failed to send broadcast message');
        } finally {
            setBroadcastSending(false);
        }
    };

    // Re-send the last broadcast only to the users that failed, so successful
    // recipients don't get the message twice.
    const handleRetryBroadcastFailures = async () => {
        if (!broadcastResult?.message || !broadcastResult.errors?.length) return;
        const failedUserIds = broadcastResult.errors.map((err) => err.userId).filter(Boolean);
        if (failedUserIds.length === 0) return;
        const message = broadcastResult.message;
        setBroadcastSending(true);
        setBroadcastError("");
        try {
            const response = await fetch('/api/admin/broadcast-message', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminPassword: 'Unicorn2025!',
                    message,
                    onlyUserIds: failedUserIds
                })
            });
            const data = await response.json();
            if (data.ok) {
                setBroadcastResult({ ...data, message, isRetry: true });
            } else {
                setBroadcastError(data.error || 'Retry failed');
            }
        } catch (err) {
            console.error('Error retrying broadcast:', err);
            setBroadcastError('Failed to retry broadcast');
        } finally {
            setBroadcastSending(false);
        }
    };

    const getCollabDateRange = (preset) => {
        const now = new Date();
        const end = now.toISOString();
        let start;
        if (preset === '7d') start = new Date(Date.now() - 7 * 86400000).toISOString();
        else if (preset === '90d') start = new Date(Date.now() - 90 * 86400000).toISOString();
        else start = new Date(Date.now() - 30 * 86400000).toISOString();
        return { start, end };
    };

    const fetchCollabAnalytics = async (presetOverride) => {
        setLoadingCollabAnalytics(true);
        setCollabAnalyticsError("");
        try {
            const preset = presetOverride || collabDatePreset;
            const { start, end } = getCollabDateRange(preset);
            const response = await fetch('/api/admin/collab-metrics', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminPassword: 'Unicorn2025!',
                    startDate: start,
                    endDate: end,
                    groupBy: collabGroupBy,
                    status: collabStatusFilter,
                    search: collabSearch,
                    limit: 25,
                    offset: collabPage * 25,
                })
            });
            const data = await response.json();
            if (data.ok) {
                setCollabAnalytics(data);
            } else {
                setCollabAnalyticsError(data.error || 'Failed to load collab analytics');
            }
        } catch (err) {
            console.error('Error fetching collab analytics:', err);
            setCollabAnalyticsError('Failed to load collaboration analytics');
        } finally {
            setLoadingCollabAnalytics(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) return;
        fetchCollabAnalytics();
    }, [collabGroupBy, collabStatusFilter, collabPage, collabDatePreset]);

    useEffect(() => {
        if (!isAuthenticated) return;
        const debounceId = setTimeout(() => {
            setCollabPage(0);
            fetchCollabAnalytics();
        }, 400);
        return () => clearTimeout(debounceId);
    }, [collabSearch]);

    // Featured Submissions fetch
    const fetchFeaturedSubmissions = async () => {
        setFeaturedLoading(true);
        try {
            const response = await fetch('/api/featured/admin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: 'Unicorn2025!', action: 'list', status: featuredFilter }),
            });
            const data = await response.json();
            if (data.ok) {
                setFeaturedSubmissions(data.submissions || []);
                setFeaturedCounts(data.counts || { pending: 0, approved: 0, featured: 0, declined: 0, total: 0 });
            }
        } catch (err) {
            console.error('Error fetching featured submissions:', err);
        } finally {
            setFeaturedLoading(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) return;
        fetchFeaturedSubmissions();
    }, [featuredFilter, isAuthenticated]);

    const handleFeaturedStatusUpdate = async (submissionId, newStatus) => {
        const adminNote = featuredNoteId === submissionId ? featuredNoteText : undefined;
        try {
            const response = await fetch('/api/featured/admin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminPassword: 'Unicorn2025!',
                    action: 'update-status',
                    submissionId,
                    newStatus,
                    adminNote,
                }),
            });
            const data = await response.json();
            if (data.ok) {
                setFeaturedNoteId(null);
                setFeaturedNoteText('');
                fetchFeaturedSubmissions();
            }
        } catch (err) {
            console.error('Error updating featured status:', err);
        }
    };

    // Collab Uploads fetch
    const fetchCollabUploads = async (kindOverride) => {
        setLoadingCollabUploads(true);
        setCollabUploadsError("");
        try {
            const response = await fetch('/api/admin/collab-uploads', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminPassword: 'Unicorn2025!',
                    kind: kindOverride || collabUploadsKind,
                    search: collabUploadsSearch,
                    limit: 120,
                }),
            });
            const data = await response.json();
            if (data.ok) {
                setCollabUploads(data.uploads || []);
                setCollabUploadsSummary(data.summary || { files: 0, links: 0 });
            } else {
                setCollabUploadsError(data.error || 'Failed to load collab uploads');
            }
        } catch (err) {
            console.error('Error fetching collab uploads:', err);
            setCollabUploadsError('Failed to load collaboration uploads');
        } finally {
            setLoadingCollabUploads(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) return;
        fetchCollabUploads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collabUploadsKind, isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) return;
        const debounceId = setTimeout(() => {
            fetchCollabUploads();
        }, 400);
        return () => clearTimeout(debounceId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collabUploadsSearch]);

    const fetchReviewCards = async () => {
        setLoadingReviewCards(true);
        setReviewCardsError('');
        try {
            const response = await fetch('/api/admin/review-cards', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: 'Unicorn2025!' }),
            });
            const data = await response.json();
            if (data.ok) {
                setReviewCards(data.reviews || []);
                setReviewCardsSummary(data.summary || { total: 0, pending: 0, accepted: 0, missingPhoto: 0 });
            } else {
                setReviewCardsError(data.error || 'Failed to load reviews');
            }
        } catch (err) {
            console.error('Error fetching review cards:', err);
            setReviewCardsError('Failed to load reviews');
        } finally {
            setLoadingReviewCards(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) return;
        fetchReviewCards();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    // Keep grid + summary in sync after the share modal saves a new photo.
    // `changes` is { heroImageUrl?, circleImageUrl? }.
    const handleAdminHeroUpdated = (changes) => {
        if (!shareCardReview) return;
        const setBg = Object.prototype.hasOwnProperty.call(changes, 'heroImageUrl');
        setReviewCards(prev => prev.map(r => {
            if (!(r.id === shareCardReview.id && r.reviewType === shareCardReview.reviewType)) return r;
            const next = { ...r };
            if (setBg) next.heroImageUrl = changes.heroImageUrl;
            if (Object.prototype.hasOwnProperty.call(changes, 'circleImageUrl')) next.circleImageUrl = changes.circleImageUrl;
            return next;
        }));
        // "Needs photo" tracks the background photo specifically.
        if (setBg) {
            setReviewCardsSummary(prev => ({
                ...prev,
                missingPhoto: Math.max(0, prev.missingPhoto - (changes.heroImageUrl ? 1 : 0)),
            }));
        }
    };

    const sortedCollabs = React.useMemo(() => {
        if (!collabAnalytics?.collabs) return [];
        const list = [...collabAnalytics.collabs];
        list.sort((a, b) => {
            let va, vb;
            if (collabSortField === 'progress') {
                va = a.progress?.percent || 0;
                vb = b.progress?.percent || 0;
            } else if (collabSortField === 'ageDays') {
                va = a.ageDays || 0;
                vb = b.ageDays || 0;
            } else if (collabSortField === 'lastActivityAt') {
                va = new Date(a.lastActivityAt || 0).getTime();
                vb = new Date(b.lastActivityAt || 0).getTime();
            } else {
                va = new Date(a.createdAt || 0).getTime();
                vb = new Date(b.createdAt || 0).getTime();
            }
            return collabSortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
        });
        return list;
    }, [collabAnalytics?.collabs, collabSortField, collabSortDir]);

    const handleCollabSort = (field) => {
        if (collabSortField === field) {
            setCollabSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setCollabSortField(field);
            setCollabSortDir('desc');
        }
    };

    const exportToCSV = (subscriptions, type) => {
        if (!subscriptions || subscriptions.length === 0) {
            alert('No data to export');
            return;
        }

        // Create CSV header
        const headers = ['Email', 'First Name', 'Last Name', 'Subscription Status', 'Plan Name', 'Current Period End'];
        
        // Create CSV rows
        const rows = subscriptions.map(sub => [
            sub.email || '',
            sub.first_name || '',
            sub.last_name || '',
            sub.subscription_status || '',
            sub.plan_name || '',
            sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : ''
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${type}-subscriptions-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportAbandonedCartToCSV = () => {
        if (!abandonedCartUsers || abandonedCartUsers.length === 0) {
            alert('No data to export');
            return;
        }

        // Create CSV header
        const headers = ['Email', 'First Name', 'Last Name', 'Roles', 'Signup Date', 'Last Subscription Status', 'Last Subscription Date'];
        
        // Create CSV rows
        const rows = abandonedCartUsers.map(user => {
            // Handle roles - ensure it's an array
            let rolesStr = '';
            if (user.roles) {
                if (Array.isArray(user.roles)) {
                    rolesStr = user.roles.join(', ');
                } else if (typeof user.roles === 'string') {
                    // Handle PostgreSQL array string format like "{creator,host}"
                    rolesStr = user.roles.replace(/[{}]/g, '').replace(/,/g, ', ');
                } else {
                    rolesStr = String(user.roles);
                }
            }
            
            return [
                user.email || '',
                user.first_name || '',
                user.last_name || '',
                rolesStr,
                user.signup_date ? new Date(user.signup_date).toLocaleDateString() : '',
                user.last_subscription_status || 'none',
                user.last_subscription_date ? new Date(user.last_subscription_date).toLocaleDateString() : ''
            ];
        });

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `abandoned-cart-users-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (isLoading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <p>Loading...</p>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="admin-password-form">
                <div className="admin-password-container">
                    <h1>Admin Access</h1>
                    <p>Please enter the admin password to continue.</p>
                    <form onSubmit={handlePasswordSubmit}>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter admin password"
                            className="admin-password-input"
                            autoFocus
                        />
                        {error && <div className="admin-error">{error}</div>}
                        <button type="submit" className="admin-submit-btn">
                            Access Admin
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // Admin page content
    const activeNav = ADMIN_NAV.find((n) => n.key === activeSection);
    const webAnalyticsSummary = vercelAnalytics?.summary || {};
    const webTimeSeries = vercelAnalytics?.timeSeries || [];
    const webDeviceData = (vercelAnalytics?.deviceBreakdown || []).map((item) => ({
        ...item,
        name: formatDeviceName(item.name),
    }));
    const webTopPages = (vercelAnalytics?.topPages || []).map((item) => ({
        ...item,
        name: shortenAnalyticsLabel(item.name),
    }));
    const webReferrers = (vercelAnalytics?.referrers || []).map((item) => ({
        ...item,
        name: shortenAnalyticsLabel(item.name),
    }));
    const webBreakdownGroups = [
        { title: 'Countries', rows: vercelAnalytics?.countries || [] },
        { title: 'Browsers', rows: vercelAnalytics?.browsers || [] },
        { title: 'Operating Systems', rows: vercelAnalytics?.operatingSystems || [] },
    ];
    return (
        <div className="admin-page admin-dashboard">
            <aside className="admin-sidebar">
                <div className="admin-sidebar-brand">Postaway Admin</div>
                <nav className="admin-nav">
                    {ADMIN_NAV.map((item) => {
                        const Icon = item.icon;
                        return (
                            <button
                                key={item.key}
                                type="button"
                                className={`admin-nav-item ${activeSection === item.key ? 'active' : ''}`}
                                onClick={() => setActiveSection(item.key)}
                            >
                                <Icon size={18} />
                                <span>{item.label}</span>
                            </button>
                        );
                    })}
                </nav>
                <div className="admin-nav-divider" />
                <nav className="admin-nav">
                    <a className="admin-nav-item" href="/admin/partner-applications">
                        <Handshake size={18} />
                        <span>Partner Applications</span>
                    </a>
                    <a className="admin-nav-item" href="/admin/users/merge">
                        <Users size={18} />
                        <span>Merge Users</span>
                    </a>
                </nav>
            </aside>

            <main className="admin-main">
            <h1>{activeNav?.label || 'Admin Dashboard'}</h1>

            {activeSection === 'overview' && (<>
            <div className="admin-section web-analytics-section">
                <div className="web-analytics-header">
                    <div>
                        <p className="web-analytics-eyebrow">Vercel Web Analytics</p>
                        <h2>Traffic Overview</h2>
                        <p className="web-analytics-subtitle">
                            Page views, unique visitors, device mix, and acquisition trends from the Vercel analytics drain.
                        </p>
                    </div>
                    <div className="web-analytics-controls">
                        <div className="ca-filter-group">
                            <span className="ca-filter-label">Range</span>
                            <div className="ca-btn-group">
                                {['7d', '30d', '90d', 'custom'].map((preset) => (
                                    <button
                                        key={preset}
                                        type="button"
                                        className={`ca-btn-toggle ${vercelDatePreset === preset ? 'active' : ''}`}
                                        onClick={() => setVercelDatePreset(preset)}
                                    >
                                        {preset === 'custom' ? 'Custom' : preset}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="ca-filter-group">
                            <span className="ca-filter-label">Group by</span>
                            <div className="ca-btn-group">
                                {['day', 'week', 'month'].map((group) => (
                                    <button
                                        key={group}
                                        type="button"
                                        className={`ca-btn-toggle ${vercelGroupBy === group ? 'active' : ''}`}
                                        onClick={() => setVercelGroupBy(group)}
                                    >
                                        {group}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {vercelDatePreset === 'custom' && (
                    <div className="web-analytics-date-row">
                        <label>
                            Start
                            <input
                                type="date"
                                value={vercelCustomStart}
                                onChange={(e) => setVercelCustomStart(e.target.value)}
                            />
                        </label>
                        <label>
                            End
                            <input
                                type="date"
                                value={vercelCustomEnd}
                                onChange={(e) => setVercelCustomEnd(e.target.value)}
                            />
                        </label>
                    </div>
                )}

                {loadingVercelAnalytics ? (
                    <p className="web-analytics-empty">Loading web analytics...</p>
                ) : vercelAnalyticsError ? (
                    <div className="admin-error">{vercelAnalyticsError}</div>
                ) : vercelAnalytics?.setupRequired ? (
                    <div className="web-analytics-empty">
                        Run migration 114 and configure the Vercel Web Analytics Drain to start populating this dashboard.
                    </div>
                ) : !vercelAnalytics?.dataAvailable ? (
                    <div className="web-analytics-empty">
                        No Vercel analytics events have arrived for this range yet. Data will appear here after the drain receives traffic.
                    </div>
                ) : (
                    <>
                        <div className="web-kpi-grid">
                            <div className="web-kpi-card web-kpi-primary">
                                <span className="web-kpi-label">Visitors</span>
                                <strong>{formatCompactNumber(webAnalyticsSummary.visitors)}</strong>
                                <span>Unique devices</span>
                            </div>
                            <div className="web-kpi-card">
                                <span className="web-kpi-label">Page Views</span>
                                <strong>{formatCompactNumber(webAnalyticsSummary.pageViews)}</strong>
                                <span>Tracked page loads</span>
                            </div>
                            <div className="web-kpi-card">
                                <span className="web-kpi-label">Sessions</span>
                                <strong>{formatCompactNumber(webAnalyticsSummary.sessions)}</strong>
                                <span>{webAnalyticsSummary.viewsPerSession || 0} views/session</span>
                            </div>
                            <div className="web-kpi-card">
                                <span className="web-kpi-label">Top Page</span>
                                <strong>{shortenAnalyticsLabel(webAnalyticsSummary.topPage?.path || '/', 22)}</strong>
                                <span>{formatCompactNumber(webAnalyticsSummary.topPage?.pageViews || 0)} views</span>
                            </div>
                        </div>

                        <div className="web-analytics-grid">
                            <div className="web-chart-card web-chart-card-wide">
                                <div className="web-chart-title">
                                    <h3>Visitors and Page Views</h3>
                                    <span>{webTimeSeries.length} periods</span>
                                </div>
                                <ResponsiveContainer width="100%" height={280}>
                                    <AreaChart data={webTimeSeries} margin={{ top: 12, right: 20, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="pageViewsGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#0f084b" stopOpacity={0.25} />
                                                <stop offset="95%" stopColor="#0f084b" stopOpacity={0.02} />
                                            </linearGradient>
                                            <linearGradient id="visitorsGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#dc1d08" stopOpacity={0.22} />
                                                <stop offset="95%" stopColor="#dc1d08" stopOpacity={0.02} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                        <XAxis dataKey="period" tickFormatter={formatDateTick} tickLine={false} axisLine={false} />
                                        <YAxis tickFormatter={formatCompactNumber} tickLine={false} axisLine={false} width={44} />
                                        <Tooltip labelFormatter={formatDateTick} formatter={(value) => formatCompactNumber(value)} />
                                        <Legend />
                                        <Area type="monotone" dataKey="pageViews" name="Page views" stroke="#0f084b" fill="url(#pageViewsGradient)" strokeWidth={3} />
                                        <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#dc1d08" fill="url(#visitorsGradient)" strokeWidth={3} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="web-chart-card">
                                <div className="web-chart-title">
                                    <h3>Device Mix</h3>
                                    <span>By page views</span>
                                </div>
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie
                                            data={webDeviceData}
                                            dataKey="pageViews"
                                            nameKey="name"
                                            innerRadius={58}
                                            outerRadius={92}
                                            paddingAngle={3}
                                        >
                                            {webDeviceData.map((entry, index) => (
                                                <Cell key={entry.name} fill={WEB_ANALYTICS_COLORS[index % WEB_ANALYTICS_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => formatCompactNumber(value)} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="web-chart-card">
                                <div className="web-chart-title">
                                    <h3>Top Pages</h3>
                                    <span>Page views</span>
                                </div>
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={webTopPages} layout="vertical" margin={{ top: 8, right: 16, left: 10, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                        <XAxis type="number" tickFormatter={formatCompactNumber} tickLine={false} axisLine={false} />
                                        <YAxis type="category" dataKey="name" width={120} tickLine={false} axisLine={false} />
                                        <Tooltip formatter={(value) => formatCompactNumber(value)} />
                                        <Bar dataKey="pageViews" name="Page views" fill="#0f084b" radius={[0, 8, 8, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="web-chart-card">
                                <div className="web-chart-title">
                                    <h3>Referrers</h3>
                                    <span>Top sources</span>
                                </div>
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={webReferrers} layout="vertical" margin={{ top: 8, right: 16, left: 10, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                        <XAxis type="number" tickFormatter={formatCompactNumber} tickLine={false} axisLine={false} />
                                        <YAxis type="category" dataKey="name" width={120} tickLine={false} axisLine={false} />
                                        <Tooltip formatter={(value) => formatCompactNumber(value)} />
                                        <Bar dataKey="visitors" name="Visitors" fill="#dc1d08" radius={[0, 8, 8, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="web-breakdown-grid">
                            {webBreakdownGroups.map((group) => {
                                const maxVisitors = Math.max(...group.rows.map((row) => row.visitors || 0), 1);
                                return (
                                    <div key={group.title} className="web-breakdown-card">
                                        <h3>{group.title}</h3>
                                        {group.rows.length > 0 ? group.rows.map((row) => (
                                            <div key={row.name} className="web-breakdown-row">
                                                <div className="web-breakdown-meta">
                                                    <span>{shortenAnalyticsLabel(row.name, 26)}</span>
                                                    <strong>{formatCompactNumber(row.visitors)} visitors</strong>
                                                </div>
                                                <div className="web-breakdown-bar">
                                                    <span style={{ width: `${Math.max(((row.visitors || 0) / maxVisitors) * 100, 4)}%` }} />
                                                </div>
                                            </div>
                                        )) : (
                                            <p className="web-breakdown-empty">No data yet.</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* Platform Overview Stats */}
            {loadingMetrics ? (
                <div className="admin-section">
                    <p>Loading metrics...</p>
                </div>
            ) : platformStats && (
                <div className="admin-section">
                    <h2>Platform Overview</h2>
                    <div className="admin-stats-grid">
                        <div className="stat-card stat-card-blue-solid">
                            <div className="stat-value">{platformStats.totalUsers?.total || '—'}</div>
                            <div className="stat-label">Total Users</div>
                            <div className="stat-details">
                                <div>Creators: {platformStats.totalUsers?.creators || 0}</div>
                                <div>Hosts: {platformStats.totalUsers?.hosts || 0}</div>
                                <div>Travelers: {platformStats.totalUsers?.travelers || 0}</div>
                            </div>
                        </div>
                        <div className="stat-card stat-card-red">
                            <div className="stat-value">{platformStats.activeSubscriptions || '—'}</div>
                            <div className="stat-label">Active Subscriptions</div>
                        </div>
                        <div className="stat-card stat-card-blue">
                            <div className="stat-value">{platformStats.newSignups?.last7Days || '—'}</div>
                            <div className="stat-label">New Signups (7 days)</div>
                            <div className="stat-details">
                                <div>30 days: {platformStats.newSignups?.last30Days || 0}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Subscription Overview */}
            {loadingSubscriptions ? (
                <div className="admin-section">
                    <p>Loading subscription data...</p>
                </div>
            ) : (
                <div className="admin-section">
                    <h2>Subscription Overview</h2>
                    <div className="admin-stats-grid">
                        <div className="stat-card stat-card-red">
                            <div className="stat-value">{hostCount !== null ? hostCount : '—'}</div>
                            <div className="stat-label">Active Host Subscriptions</div>
                            {hostCount > 0 && (
                                <button 
                                    onClick={() => exportToCSV(hostSubscriptions, 'host')} 
                                    className="admin-export-btn stat-card-btn"
                                    disabled={loadingSubscriptions}
                                >
                                    Export to CSV
                                </button>
                            )}
                        </div>

                        <div className="stat-card stat-card-blue-solid">
                            <div className="stat-value">{creatorCount !== null ? creatorCount : '—'}</div>
                            <div className="stat-label">Active Creator Subscriptions</div>
                            {creatorCount > 0 && (
                                <button 
                                    onClick={() => exportToCSV(creatorSubscriptions, 'creator')} 
                                    className="admin-export-btn stat-card-btn"
                                    disabled={loadingSubscriptions}
                                >
                                    Export to CSV
                                </button>
                            )}
                        </div>

                        <div className="stat-card stat-card-blue">
                            <div className="stat-value">{travelerCount !== null ? travelerCount : '—'}</div>
                            <div className="stat-label">Active Traveler Subscriptions</div>
                            {travelerCount > 0 && (
                                <button 
                                    onClick={() => exportToCSV(travelerSubscriptions, 'traveler')} 
                                    className="admin-export-btn stat-card-btn"
                                    disabled={loadingSubscriptions}
                                >
                                    Export to CSV
                                </button>
                            )}
                        </div>

                        <div className="stat-card stat-card-gray">
                            <div className="stat-value">{loadingAbandonedCart ? '—' : (abandonedCartCount !== null ? abandonedCartCount : '—')}</div>
                            <div className="stat-label">Abandoned Cart Users</div>
                            <div className="stat-details">
                                <div>No active subscription</div>
                            </div>
                            {abandonedCartCount > 0 && (
                                <button 
                                    onClick={() => exportAbandonedCartToCSV()} 
                                    className="admin-export-btn stat-card-btn"
                                    disabled={loadingAbandonedCart}
                                >
                                    Export to CSV
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Paid hosts hidden from the /hosts page */}
            <div className="admin-section">
                <h2>Paid Hosts Hidden From /hosts</h2>
                <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 14px' }}>
                    These hosts have an active subscription but don't render on the public
                    hosts page — either they never added a property or their property has
                    no photo. Reach out and get them visible.
                    {hiddenHostsTotalPaying !== null && hiddenHosts.length > 0 && (
                        <> ({hiddenHosts.length} of {hiddenHostsTotalPaying} paying hosts)</>
                    )}
                </p>
                {loadingHiddenHosts ? (
                    <p>Loading…</p>
                ) : hiddenHosts.length === 0 ? (
                    <p style={{ fontSize: '13.5px', color: '#065f46', fontWeight: 600 }}>
                        All paying hosts are visible on the hosts page.
                    </p>
                ) : (
                    <>
                        <button
                            onClick={exportHiddenHostsToCSV}
                            className="admin-export-btn"
                            style={{ marginBottom: '12px' }}
                        >
                            Export to CSV
                        </button>
                        <div className="ca-table-wrap">
                            <table className="ca-table">
                                <thead>
                                    <tr>
                                        <th>Host</th>
                                        <th>Email</th>
                                        <th>Reason Hidden</th>
                                        <th>Profile Pic</th>
                                        <th>Properties</th>
                                        <th>Status</th>
                                        <th>Subscribed</th>
                                        <th>Nudge</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {hiddenHosts.map(h => (
                                        <tr key={h.userId} className="ca-row" style={{ cursor: 'default' }}>
                                            <td>{h.brandName || (h.handle ? `@${h.handle}` : (h.firstName || '—'))}</td>
                                            <td>
                                                <a
                                                    href={`mailto:${h.email}?subject=${encodeURIComponent('Missing Profile Pic - Postaway')}`}
                                                    style={{ color: '#2563eb', textDecoration: 'underline' }}
                                                >
                                                    {h.email}
                                                </a>
                                            </td>
                                            <td>
                                                <span style={{
                                                    padding: '3px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: 600,
                                                    background: h.reason === 'No property added' ? '#fee2e2' : '#fef3c7',
                                                    color: h.reason === 'No property added' ? '#991b1b' : '#92400e',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {h.reason}
                                                </span>
                                            </td>
                                            <td>{h.hasAvatar ? '✓' : '✗ missing'}</td>
                                            <td>{h.propertyCount}</td>
                                            <td>{h.subscriptionStatus}</td>
                                            <td>{h.subscribedAt ? new Date(h.subscribedAt).toLocaleDateString() : '—'}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <button
                                                    onClick={() => sendNudge('host', h)}
                                                    disabled={nudgingUserId === h.userId}
                                                    title="Send a reminder email to add a property photo"
                                                    style={{
                                                        padding: '4px 12px', borderRadius: '100px', border: '1.5px solid #0f084b',
                                                        background: '#fff', color: '#0f084b', fontSize: '11px', fontWeight: 700,
                                                        cursor: nudgingUserId === h.userId ? 'wait' : 'pointer',
                                                    }}
                                                >
                                                    {nudgingUserId === h.userId ? 'Sending…' : (h.lastNudgedAt ? 'Nudge again' : 'Nudge')}
                                                </button>
                                                {h.lastNudgedAt && (
                                                    <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '3px' }}>
                                                        Nudged {new Date(h.lastNudgedAt).toLocaleDateString()}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* Paying creators missing a profile pic or IG handle */}
            <div className="admin-section">
                <h2>Paid Creators Missing Profile Pic / IG Handle</h2>
                <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 14px' }}>
                    These creators have an active subscription but are missing a profile
                    photo (which hides them from the public creators page) or an
                    Instagram handle.
                    {hiddenCreatorsTotalPaying !== null && hiddenCreators.length > 0 && (
                        <> ({hiddenCreators.length} of {hiddenCreatorsTotalPaying} paying creators)</>
                    )}
                </p>
                {loadingHiddenCreators ? (
                    <p>Loading…</p>
                ) : hiddenCreators.length === 0 ? (
                    <p style={{ fontSize: '13.5px', color: '#065f46', fontWeight: 600 }}>
                        All paying creators have a profile pic and IG handle.
                    </p>
                ) : (
                    <>
                        <button
                            onClick={exportHiddenCreatorsToCSV}
                            className="admin-export-btn"
                            style={{ marginBottom: '12px' }}
                        >
                            Export to CSV
                        </button>
                        <div className="ca-table-wrap">
                            <table className="ca-table">
                                <thead>
                                    <tr>
                                        <th>Creator</th>
                                        <th>Email</th>
                                        <th>Missing</th>
                                        <th>Profile Pic</th>
                                        <th>IG Handle</th>
                                        <th>Status</th>
                                        <th>Subscribed</th>
                                        <th>Nudge</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {hiddenCreators.map(c => (
                                        <tr key={c.userId} className="ca-row" style={{ cursor: 'default' }}>
                                            <td>{c.displayName || (c.handle ? `@${c.handle}` : (c.firstName || '—'))}</td>
                                            <td>
                                                <a
                                                    href={`mailto:${c.email}?subject=${encodeURIComponent('Missing Profile Info - Postaway')}`}
                                                    style={{ color: '#2563eb', textDecoration: 'underline' }}
                                                >
                                                    {c.email}
                                                </a>
                                            </td>
                                            <td>
                                                {(c.reasons || []).map(reason => (
                                                    <span key={reason} style={{
                                                        display: 'inline-block',
                                                        padding: '3px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: 600,
                                                        background: reason === 'Missing profile photo' ? '#fee2e2' : '#fef3c7',
                                                        color: reason === 'Missing profile photo' ? '#991b1b' : '#92400e',
                                                        whiteSpace: 'nowrap', marginRight: '4px', marginBottom: '2px',
                                                    }}>
                                                        {reason}
                                                    </span>
                                                ))}
                                            </td>
                                            <td>{c.hasAvatar ? '✓' : '✗ missing'}</td>
                                            <td>{c.hasInstagram ? '✓' : '✗ missing'}</td>
                                            <td>{c.subscriptionStatus}</td>
                                            <td>{c.subscribedAt ? new Date(c.subscribedAt).toLocaleDateString() : '—'}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <button
                                                    onClick={() => sendNudge('creator', c)}
                                                    disabled={nudgingUserId === c.userId}
                                                    title="Send a reminder email to complete their profile"
                                                    style={{
                                                        padding: '4px 12px', borderRadius: '100px', border: '1.5px solid #0f084b',
                                                        background: '#fff', color: '#0f084b', fontSize: '11px', fontWeight: 700,
                                                        cursor: nudgingUserId === c.userId ? 'wait' : 'pointer',
                                                    }}
                                                >
                                                    {nudgingUserId === c.userId ? 'Sending…' : (c.lastNudgedAt ? 'Nudge again' : 'Nudge')}
                                                </button>
                                                {c.lastNudgedAt && (
                                                    <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '3px' }}>
                                                        Nudged {new Date(c.lastNudgedAt).toLocaleDateString()}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* Content Metrics */}
            {contentMetrics && (
                <div className="admin-section">
                    <h2>Content Metrics</h2>
                    <div className="admin-metrics-grid">
                        <div className="metric-group">
                            <h3>Properties</h3>
                            <div className="metric-value">{contentMetrics.properties?.total || 0}</div>
                            <div className="metric-label">Avg per host: {contentMetrics.properties?.avgPerHost || '0'}</div>
                        </div>
                        <div className="metric-group">
                            <h3>Profiles</h3>
                            <div className="metric-value">{contentMetrics.profiles?.creators || 0}</div>
                            <div className="metric-label">Creators</div>
                            <div className="metric-value-small">{contentMetrics.profiles?.hosts || 0}</div>
                            <div className="metric-label-small">Hosts</div>
                        </div>
                        <div className="metric-group">
                            <h3>Reviews</h3>
                            <div className="metric-value">{contentMetrics.reviews?.total || 0}</div>
                            <div className="metric-label">
                                Property: {contentMetrics.reviews?.property || 0} | 
                                Creator: {contentMetrics.reviews?.creator || 0} | 
                                Host: {contentMetrics.reviews?.host || 0}
                            </div>
                            {contentMetrics.reviews?.pendingHostReviews > 0 && (
                                <div className="metric-alert">
                                    {contentMetrics.reviews.pendingHostReviews} pending host reviews
                                </div>
                            )}
                        </div>
                        <div className="metric-group">
                            <h3>Average Ratings</h3>
                            <div className="metric-value">{contentMetrics.averageRatings?.property || '0.00'}</div>
                            <div className="metric-label">Property</div>
                            <div className="metric-value-small">{contentMetrics.averageRatings?.creator || '0.00'}</div>
                            <div className="metric-label-small">Creator</div>
                            <div className="metric-value-small">{contentMetrics.averageRatings?.host || '0.00'}</div>
                            <div className="metric-label-small">Host</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Engagement Metrics */}
            {engagementMetrics && (
                <div className="admin-section">
                    <h2>Engagement Metrics</h2>
                    <div className="admin-metrics-grid">
                        <div className="metric-group">
                            <h3>Messaging</h3>
                            <div className="metric-value">{engagementMetrics.messaging?.totalMessages || 0}</div>
                            <div className="metric-label">Total Messages</div>
                            <div className="metric-value-small">{engagementMetrics.messaging?.activeConversations || 0}</div>
                            <div className="metric-label-small">Active Conversations (30d)</div>
                        </div>
                        <div className="metric-group">
                            <h3>Likes</h3>
                            <div className="metric-value">{engagementMetrics.likes?.totalCollabLikes || 0}</div>
                            <div className="metric-label">Total Collab Likes</div>
                        </div>
                        <div className="metric-group">
                            <h3>Itineraries</h3>
                            <div className="metric-value">{engagementMetrics.itineraries?.total || 0}</div>
                            <div className="metric-label">
                                Creator: {engagementMetrics.itineraries?.creator || 0} | 
                                Host: {engagementMetrics.itineraries?.host || 0}
                            </div>
                        </div>
                        <div className="metric-group">
                            <h3>Travel Board</h3>
                            <div className="metric-value">{engagementMetrics.travelBoard?.posts || 0}</div>
                            <div className="metric-label">Posts</div>
                        </div>
                    </div>
                    
                    {/* Top Liked Creators & Properties */}
                    {(engagementMetrics.likes?.topLikedCreators?.length > 0 || engagementMetrics.likes?.topLikedProperties?.length > 0) && (
                        <div className="top-liked-section">
                            {engagementMetrics.likes.topLikedCreators.length > 0 && (
                                <div className="top-liked-list">
                                    <h4>Top Liked Creators</h4>
                                    <ul>
                                        {engagementMetrics.likes.topLikedCreators.map((creator, idx) => (
                                            <li key={creator.id}>
                                                <span className="rank">#{idx + 1}</span>
                                                <span className="name">{creator.name}</span>
                                                <span className="count">{creator.likes} likes</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {engagementMetrics.likes.topLikedProperties.length > 0 && (
                                <div className="top-liked-list">
                                    <h4>Top Liked Properties</h4>
                                    <ul>
                                        {engagementMetrics.likes.topLikedProperties.map((property, idx) => (
                                            <li key={property.id}>
                                                <span className="rank">#{idx + 1}</span>
                                                <span className="name">{property.name}</span>
                                                <span className="count">{property.likes} likes</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            </>)}

            {activeSection === 'broadcast' && (
            <div className="admin-section">
                <h2>Broadcast Message</h2>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                    Send a message to every creator and host on the platform via the Official Postaway Channel. Recipients will also receive an email notification.
                </p>

                {broadcastError && (
                    <div className="admin-error" style={{ marginBottom: '16px' }}>{broadcastError}</div>
                )}

                {broadcastResult && (
                    <div className="broadcast-result">
                        <div className="broadcast-result-stat broadcast-result-sent">
                            {broadcastResult.isRetry ? 'Retry — sent' : 'Sent'}: {broadcastResult.sent} / {broadcastResult.total}
                        </div>
                        {broadcastResult.failed > 0 && (
                            <div className="broadcast-result-stat broadcast-result-failed">
                                Failed: {broadcastResult.failed}
                                {broadcastResult.message && (
                                    <button
                                        className="broadcast-confirm-btn broadcast-confirm-yes"
                                        style={{ marginLeft: '12px' }}
                                        onClick={handleRetryBroadcastFailures}
                                        disabled={broadcastSending}
                                    >
                                        {broadcastSending ? 'Retrying...' : 'Retry failed only'}
                                    </button>
                                )}
                            </div>
                        )}
                        {broadcastResult.errors?.length > 0 && (
                            <details className="broadcast-errors-details">
                                <summary>View failures ({broadcastResult.errors.length})</summary>
                                <ul className="broadcast-errors-list">
                                    {broadcastResult.errors.map((err, idx) => (
                                        <li key={idx}>{err.email}: {err.error}</li>
                                    ))}
                                </ul>
                            </details>
                        )}
                    </div>
                )}

                <div className="broadcast-form">
                    <textarea
                        className="broadcast-textarea"
                        value={broadcastDraft}
                        onChange={(e) => {
                            setBroadcastDraft(e.target.value);
                            setBroadcastConfirming(false);
                            setBroadcastResult(null);
                            setBroadcastError("");
                        }}
                        placeholder="Type your broadcast message..."
                        rows={4}
                        maxLength={2000}
                        disabled={broadcastSending}
                    />
                    <div className="broadcast-footer">
                        <span className="broadcast-char-count">
                            {broadcastDraft.length} / 2000
                        </span>
                        {!broadcastConfirming ? (
                            <button
                                className="admin-submit-btn broadcast-send-btn"
                                onClick={() => setBroadcastConfirming(true)}
                                disabled={!broadcastDraft.trim() || broadcastSending}
                            >
                                Send to All Users
                            </button>
                        ) : (
                            <div className="broadcast-confirm-group">
                                <span className="broadcast-confirm-label">Are you sure?</span>
                                <button
                                    className="broadcast-confirm-btn broadcast-confirm-yes"
                                    onClick={handleSendBroadcast}
                                    disabled={broadcastSending}
                                >
                                    {broadcastSending ? 'Sending...' : 'Yes, Send Now'}
                                </button>
                                <button
                                    className="broadcast-confirm-btn broadcast-confirm-cancel"
                                    onClick={() => setBroadcastConfirming(false)}
                                    disabled={broadcastSending}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            )}

            {activeSection === 'emails' && (<>
            <div className="admin-section">
                <h2>Email Controls</h2>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                    Global kill switches for every non-essential email Postaway sends.
                    A category that is off sends nothing, regardless of individual member
                    notification preferences. Changes take effect immediately.
                </p>

                {emailFlagsError && (
                    <div className="admin-error" style={{ marginBottom: '16px' }}>{emailFlagsError}</div>
                )}
                {loadingEmailFlags && emailFlags.length === 0 && (
                    <div style={{ color: '#6b7280', fontSize: 14 }}>Loading email controls…</div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {emailFlags.map((flag) => (
                        <div
                            key={flag.key}
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 16,
                                padding: '14px 16px',
                                border: '1px solid #e5e7eb',
                                borderRadius: 12,
                                background: flag.enabled ? '#ffffff' : '#f9fafb',
                                flexWrap: 'wrap',
                            }}
                        >
                            <div style={{ flex: '1 1 260px', minWidth: 200 }}>
                                <div style={{ fontWeight: 700, color: '#0f084b', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    {flag.label}
                                    <span style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        padding: '2px 8px',
                                        borderRadius: 999,
                                        background: flag.enabled ? '#dcfce7' : '#fee2e2',
                                        color: flag.enabled ? '#15803d' : '#b91c1c',
                                    }}>
                                        {flag.enabled ? 'ON' : 'OFF'}
                                    </span>
                                </div>
                                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>
                                    {flag.description}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => handleToggleEmailFlag(flag.key, !flag.enabled)}
                                disabled={savingEmailFlagKey === flag.key}
                                aria-pressed={flag.enabled}
                                style={{
                                    position: 'relative',
                                    width: 52,
                                    height: 28,
                                    borderRadius: 999,
                                    border: 'none',
                                    flexShrink: 0,
                                    cursor: savingEmailFlagKey === flag.key ? 'wait' : 'pointer',
                                    background: flag.enabled ? '#15803d' : '#d1d5db',
                                    transition: 'background 0.15s ease',
                                    opacity: savingEmailFlagKey === flag.key ? 0.6 : 1,
                                }}
                            >
                                <span style={{
                                    position: 'absolute',
                                    top: 3,
                                    left: flag.enabled ? 27 : 3,
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    background: '#ffffff',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                    transition: 'left 0.15s ease',
                                }} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            <div className="admin-section">
                <h2>Always-On Emails</h2>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                    These transactional emails are essential to core account flows and
                    cannot be disabled.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {essentialEmails.map((item) => (
                        <div
                            key={item.label}
                            style={{
                                padding: '12px 16px',
                                border: '1px solid #e5e7eb',
                                borderRadius: 12,
                                background: '#ffffff',
                            }}
                        >
                            <div style={{ fontWeight: 700, color: '#0f084b', fontSize: 14 }}>{item.label}</div>
                            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>{item.description}</div>
                        </div>
                    ))}
                </div>
            </div>
            </>)}

            {activeSection === 'events' && <SummitAttendees />}
            {activeSection === 'academy' && <AcademyAdmin />}
            {activeSection === 'innerCircle' && <InnerCircleAdmin />}
            {activeSection === 'spotlight' && <SpotlightAdmin />}

            {activeSection === 'settings' && (<>
            <div className="admin-section">
                <h2>Social Share Image</h2>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                    Upload the image that appears when a Postaway link is shared on
                    iMessage, Facebook, LinkedIn, Slack, X, and other platforms. New
                    shares will use this photo (existing previews may stay cached for a
                    short time). Recommended size 1200×630px, JPG or PNG, under 10MB.
                </p>

                {siteMetaError && (
                    <div className="admin-error" style={{ marginBottom: '16px' }}>{siteMetaError}</div>
                )}
                {siteMetaSuccess && (
                    <div style={{ marginBottom: '16px', color: '#15803d', fontWeight: 600 }}>{siteMetaSuccess}</div>
                )}

                <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div
                        style={{
                            width: 320,
                            maxWidth: '100%',
                            aspectRatio: '1200 / 630',
                            borderRadius: 12,
                            background: '#f5f5f5',
                            border: '1px dashed #d1d5db',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            flexShrink: 0,
                        }}
                    >
                        {(siteOgPreviewUrl || siteOgImageUrl) ? (
                            <img
                                src={siteOgPreviewUrl || siteOgImageUrl}
                                alt="Social share preview"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                        ) : (
                            <span style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: 12, lineHeight: 1.3 }}>
                                {loadingSiteMeta ? 'Loading…' : 'Using platform default image'}
                            </span>
                        )}
                        {(uploadingSiteOg || savingSiteOg) && (
                            <div style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(255,255,255,0.75)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 13,
                                color: '#0f084b',
                                fontWeight: 600,
                            }}>
                                {uploadingSiteOg ? 'Uploading…' : 'Saving…'}
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 200 }}>
                        <input
                            ref={siteOgInputRef}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif"
                            onChange={handleSiteOgFileSelect}
                            style={{ display: 'none' }}
                        />
                        <button
                            type="button"
                            className="admin-submit-btn"
                            disabled={uploadingSiteOg || savingSiteOg}
                            onClick={() => siteOgInputRef.current?.click()}
                        >
                            {siteOgImageUrl ? 'Replace Image' : 'Upload Image'}
                        </button>
                        {siteOgImageUrl && (
                            <button
                                type="button"
                                onClick={handleResetSiteOg}
                                disabled={uploadingSiteOg || savingSiteOg}
                                style={{
                                    padding: '8px 12px',
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: '#b91c1c',
                                    background: 'transparent',
                                    border: '1px solid #fecaca',
                                    borderRadius: 8,
                                    cursor: (uploadingSiteOg || savingSiteOg) ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Reset to Default
                            </button>
                        )}
                        {siteOgImageUrl && (
                            <a
                                href={siteOgImageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: 12, color: '#6b7280', wordBreak: 'break-all' }}
                            >
                                View current image
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Checkout Carousel Manager */}
            <div className="admin-section">
                <h2>Checkout Carousel Manager</h2>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                    Update the trusted profile carousel shown across creator, host, and traveler checkout pages.
                </p>

                {checkoutCarouselError && (
                    <div className="admin-error carousel-inline-error">{checkoutCarouselError}</div>
                )}
                {checkoutCarouselSuccess && (
                    <div className="admin-success">{checkoutCarouselSuccess}</div>
                )}

                <div className="checkout-carousel-toolbar">
                    <div className="checkout-carousel-count">
                        {checkoutCarouselProfiles.length} profiles selected
                    </div>
                    <button
                        type="button"
                        className="admin-submit-btn checkout-carousel-save-btn"
                        onClick={handleSaveCheckoutCarousel}
                        disabled={savingCheckoutCarousel || loadingCheckoutCarousel}
                    >
                        {savingCheckoutCarousel ? 'Saving...' : 'Save Carousel Changes'}
                    </button>
                </div>

                <div className="checkout-carousel-search">
                    <input
                        type="text"
                        value={checkoutCarouselSearch}
                        onChange={(e) => setCheckoutCarouselSearch(e.target.value)}
                        placeholder="Search by first name and/or handle..."
                        className="checkout-carousel-search-input"
                    />
                </div>

                {checkoutCarouselSearch.trim().length > 0 && (
                    <div className="checkout-carousel-search-results">
                        {loadingCheckoutCarouselSearch ? (
                            <p className="checkout-carousel-muted">Searching profiles...</p>
                        ) : checkoutCarouselSearchResults.length === 0 ? (
                            <p className="checkout-carousel-muted">No matching profiles found.</p>
                        ) : (
                            checkoutCarouselSearchResults.map((profile) => {
                                const alreadyAdded = isAlreadyInCheckoutCarousel(profile);
                                return (
                                    <div key={profile.local_id} className="checkout-carousel-search-item">
                                        <img
                                            src={profile.preview_image_url || profile.image_url}
                                            alt={profile.handle}
                                            className="checkout-carousel-search-avatar"
                                            loading="lazy"
                                        />
                                        <div className="checkout-carousel-search-meta">
                                            <div className="checkout-carousel-search-handle">{profile.handle}</div>
                                            <div className="checkout-carousel-search-subtext">
                                                {profile.full_name || profile.brand_name || profile.source_type || "profile"}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="checkout-carousel-add-btn"
                                            onClick={() => handleAddCheckoutCarouselProfile(profile)}
                                            disabled={alreadyAdded}
                                        >
                                            {alreadyAdded ? "Added" : "Add"}
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                <div className="checkout-carousel-list-wrap">
                    <h3 className="checkout-carousel-list-title">Current order (drag to reorder)</h3>
                    {loadingCheckoutCarousel ? (
                        <p className="checkout-carousel-muted">Loading current carousel profiles...</p>
                    ) : checkoutCarouselProfiles.length === 0 ? (
                        <p className="checkout-carousel-muted">No profiles selected yet.</p>
                    ) : (
                        <DndContext
                            sensors={carouselSensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleCheckoutCarouselDragEnd}
                        >
                            <SortableContext
                                items={checkoutCarouselProfiles.map((profile) => profile.local_id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="checkout-carousel-list">
                                    {checkoutCarouselProfiles.map((profile, index) => (
                                        <SortableCarouselProfileRow
                                            key={profile.local_id}
                                            id={profile.local_id}
                                            profile={profile}
                                            index={index}
                                            onRemove={handleRemoveCheckoutCarouselProfile}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    )}
                </div>
            </div>

            </>)}

            {activeSection === 'pulse' && (
            <div className="admin-section">
                <h2>Live Pulse Manager</h2>
                <p style={{ marginBottom: '20px', color: '#666' }}>
                    Manage the messages that appear in the Live Pulse ticker (top-right corner).
                </p>
                
                {pulseError && (
                    <div className="admin-error" style={{ marginBottom: '16px' }}>{pulseError}</div>
                )}

                {/* Placeholder Messages Section */}
                <div className="pulse-subsection">
                    <h3>Placeholder Messages ({pulseMessages.length})</h3>
                    <p className="pulse-description">
                        These messages are shown in rotation when there's no recent activity.
                    </p>
                    
                    {/* Add new message form */}
                    <div className="pulse-add-form">
                        <input
                            type="text"
                            value={newPulseMessage}
                            onChange={(e) => setNewPulseMessage(e.target.value)}
                            placeholder="Enter new pulse message..."
                            className="pulse-input"
                            onKeyDown={(e) => e.key === 'Enter' && handleCreatePulseMessage()}
                        />
                        <button 
                            onClick={handleCreatePulseMessage}
                            className="pulse-btn pulse-btn-add"
                            disabled={!newPulseMessage.trim()}
                        >
                            Add Message
                        </button>
                    </div>

                    {loadingPulseMessages ? (
                        <p>Loading messages...</p>
                    ) : (
                        <div className="pulse-list">
                            {pulseMessages.map((msg) => (
                                <div key={msg.id} className={`pulse-item ${!msg.is_enabled ? 'pulse-item-disabled' : ''}`}>
                                    {editingMessageId === msg.id ? (
                                        <div className="pulse-edit-row">
                                            <input
                                                type="text"
                                                value={editingMessageText}
                                                onChange={(e) => setEditingMessageText(e.target.value)}
                                                className="pulse-input pulse-input-edit"
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleUpdatePulseMessage(msg.id);
                                                    if (e.key === 'Escape') {
                                                        setEditingMessageId(null);
                                                        setEditingMessageText("");
                                                    }
                                                }}
                                            />
                                            <button 
                                                onClick={() => handleUpdatePulseMessage(msg.id)}
                                                className="pulse-btn pulse-btn-save"
                                            >
                                                Save
                                            </button>
                                            <button 
                                                onClick={() => {
                                                    setEditingMessageId(null);
                                                    setEditingMessageText("");
                                                }}
                                                className="pulse-btn pulse-btn-cancel"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className={`pulse-item-status ${msg.is_enabled ? 'active' : 'disabled'}`} />
                                            <span className="pulse-item-text">{msg.message}</span>
                                            <div className="pulse-item-actions">
                                                <button 
                                                    onClick={() => handleTogglePulseMessageEnabled(msg.id, msg.is_enabled)}
                                                    className={`pulse-btn pulse-btn-toggle ${msg.is_enabled ? 'pulse-btn-enabled' : 'pulse-btn-disabled-state'}`}
                                                    title={msg.is_enabled ? 'Click to disable' : 'Click to enable'}
                                                >
                                                    {msg.is_enabled ? '✓' : '○'}
                                                </button>
                                                <button 
                                                    onClick={() => {
                                                        setEditingMessageId(msg.id);
                                                        setEditingMessageText(msg.message);
                                                    }}
                                                    className="pulse-btn pulse-btn-edit"
                                                    title="Edit message"
                                                >
                                                    ✎
                                                </button>
                                                <button 
                                                    onClick={() => handleDeletePulseMessage(msg.id)}
                                                    className="pulse-btn pulse-btn-delete"
                                                    title="Delete message"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                            {pulseMessages.length === 0 && (
                                <p className="pulse-empty">No placeholder messages found. Add one above.</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Activity Feed Section */}
                <div className="pulse-subsection" style={{ marginTop: '32px' }}>
                    <h3>Recent Activity Events ({pulseActivity.length})</h3>
                    <p className="pulse-description">
                        Real-time events from user activity. You can override the displayed text or hide events.
                    </p>
                    
                    <div className="pulse-controls">
                        <label className="pulse-checkbox-label">
                            <input
                                type="checkbox"
                                checked={showHiddenActivity}
                                onChange={(e) => {
                                    setShowHiddenActivity(e.target.checked);
                                    fetchPulseActivity(e.target.checked);
                                }}
                            />
                            Show hidden events
                        </label>
                        <button 
                            onClick={() => fetchPulseActivity(showHiddenActivity)}
                            className="pulse-btn pulse-btn-refresh"
                        >
                            Refresh
                        </button>
                    </div>

                    {loadingPulseActivity ? (
                        <p>Loading activity...</p>
                    ) : (
                        <div className="pulse-list">
                            {pulseActivity.map((event) => (
                                <div key={event.id} className={`pulse-item ${event.is_hidden ? 'pulse-item-hidden' : ''}`}>
                                    {editingActivityId === event.id ? (
                                        <div className="pulse-edit-row">
                                            <div className="pulse-edit-info">
                                                <span className="pulse-event-type">{event.event_type}</span>
                                                <span className="pulse-generated-copy">Original: {event.generated_copy || 'N/A'}</span>
                                            </div>
                                            <input
                                                type="text"
                                                value={editingActivityCopy}
                                                onChange={(e) => setEditingActivityCopy(e.target.value)}
                                                className="pulse-input pulse-input-edit"
                                                placeholder="Leave empty to use auto-generated text"
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleUpdateActivityCopy(event.id);
                                                    if (e.key === 'Escape') {
                                                        setEditingActivityId(null);
                                                        setEditingActivityCopy("");
                                                    }
                                                }}
                                            />
                                            <button 
                                                onClick={() => handleUpdateActivityCopy(event.id)}
                                                className="pulse-btn pulse-btn-save"
                                            >
                                                Save
                                            </button>
                                            <button 
                                                onClick={() => {
                                                    setEditingActivityId(null);
                                                    setEditingActivityCopy("");
                                                }}
                                                className="pulse-btn pulse-btn-cancel"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className={`pulse-item-status ${event.is_hidden ? 'disabled' : 'active'}`} />
                                            <div className="pulse-item-content">
                                                <span className="pulse-event-type">{event.event_type}</span>
                                                <span className="pulse-item-text">
                                                    {event.admin_copy ? (
                                                        <><em style={{color: '#0f084b'}}>[Custom]</em> {event.admin_copy}</>
                                                    ) : (
                                                        event.generated_copy || 'Unable to generate copy'
                                                    )}
                                                </span>
                                            </div>
                                            <span className="pulse-item-time">
                                                {new Date(event.created_at).toLocaleString()}
                                            </span>
                                            <div className="pulse-item-actions">
                                                <button 
                                                    onClick={() => handleToggleActivityHidden(event.id, event.is_hidden)}
                                                    className={`pulse-btn pulse-btn-toggle ${event.is_hidden ? 'pulse-btn-disabled-state' : 'pulse-btn-enabled'}`}
                                                    title={event.is_hidden ? 'Click to show' : 'Click to hide'}
                                                >
                                                    {event.is_hidden ? '○' : '✓'}
                                                </button>
                                                <button 
                                                    onClick={() => {
                                                        setEditingActivityId(event.id);
                                                        setEditingActivityCopy(event.admin_copy || "");
                                                    }}
                                                    className="pulse-btn pulse-btn-edit"
                                                    title="Edit display text"
                                                >
                                                    ✎
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                            {pulseActivity.length === 0 && (
                                <p className="pulse-empty">No activity events found.</p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            )}

            {activeSection === 'featured' && (
            <div className="admin-section">
                <h2>Featured Photo Submissions {featuredCounts.pending > 0 && <span style={{ fontSize: '14px', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '10px', marginLeft: '8px' }}>{featuredCounts.pending} pending</span>}</h2>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                    Creators submit photos to be featured on the Postaway Instagram grid. Review and approve them here.
                </p>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    {[
                        { key: 'all', label: 'All', count: featuredCounts.total },
                        { key: 'pending', label: 'Pending', count: featuredCounts.pending },
                        { key: 'approved', label: 'Approved', count: featuredCounts.approved },
                        { key: 'featured', label: 'Featured', count: featuredCounts.featured },
                        { key: 'declined', label: 'Declined', count: featuredCounts.declined },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setFeaturedFilter(tab.key)}
                            style={{
                                padding: '6px 14px',
                                borderRadius: '6px',
                                border: featuredFilter === tab.key ? '2px solid #0f084b' : '1px solid #d1d5db',
                                background: featuredFilter === tab.key ? '#0f084b' : '#fff',
                                color: featuredFilter === tab.key ? '#fff' : '#374151',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            {tab.label} ({tab.count})
                        </button>
                    ))}
                </div>

                {featuredLoading ? (
                    <p style={{ color: '#999' }}>Loading submissions...</p>
                ) : featuredSubmissions.length === 0 ? (
                    <p style={{ color: '#999', padding: '20px 0' }}>No submissions found.</p>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                        {featuredSubmissions.map(sub => {
                            const statusColors = {
                                pending:  { bg: '#fef3c7', color: '#92400e' },
                                approved: { bg: '#d1fae5', color: '#065f46' },
                                featured: { bg: '#dbeafe', color: '#1e40af' },
                                declined: { bg: '#f3f4f6', color: '#6b7280' },
                            };
                            const sc = statusColors[sub.status] || statusColors.pending;
                            return (
                                <div key={sub.id} style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}>
                                    <div style={{ width: '100%', aspectRatio: '4/3', overflow: 'hidden', background: '#f3f4f6' }}>
                                        <a href={sub.image_url} target="_blank" rel="noopener noreferrer">
                                            <img src={sub.image_url} alt="Submission" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                        </a>
                                    </div>
                                    <div style={{ padding: '12px 16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                            <strong style={{ fontSize: '14px', color: '#0f084b' }}>
                                                {sub.creator_handle ? `@${sub.creator_handle}` : (sub.creator_name || 'Creator')}
                                            </strong>
                                            <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: sc.bg, color: sc.color }}>
                                                {sub.status}
                                            </span>
                                        </div>
                                        {sub.host_handle && (
                                            <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#6b7280' }}>Host: @{sub.host_handle}</p>
                                        )}
                                        {sub.caption && (
                                            <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#374151' }}>{sub.caption}</p>
                                        )}
                                        <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#9ca3af' }}>
                                            {new Date(sub.created_at).toLocaleDateString()}
                                        </p>

                                        {/* Admin note toggle */}
                                        {featuredNoteId === sub.id ? (
                                            <div style={{ marginBottom: '8px' }}>
                                                <textarea
                                                    value={featuredNoteText}
                                                    onChange={e => setFeaturedNoteText(e.target.value)}
                                                    placeholder="Optional note (shown to creator on decline)"
                                                    style={{ width: '100%', padding: '6px 8px', fontSize: '13px', borderRadius: '6px', border: '1px solid #d1d5db', resize: 'vertical', minHeight: '50px' }}
                                                />
                                            </div>
                                        ) : sub.admin_note ? (
                                            <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>Note: {sub.admin_note}</p>
                                        ) : null}

                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                            {sub.status !== 'approved' && (
                                                <button onClick={() => handleFeaturedStatusUpdate(sub.id, 'approved')} style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', border: 'none', background: '#d1fae5', color: '#065f46', cursor: 'pointer' }}>
                                                    Approve
                                                </button>
                                            )}
                                            {sub.status !== 'featured' && (
                                                <button onClick={() => handleFeaturedStatusUpdate(sub.id, 'featured')} style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', border: 'none', background: '#dbeafe', color: '#1e40af', cursor: 'pointer' }}>
                                                    Mark Featured
                                                </button>
                                            )}
                                            {sub.status !== 'declined' && (
                                                <button onClick={() => { setFeaturedNoteId(sub.id); setFeaturedNoteText(sub.admin_note || ''); }} style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', border: 'none', background: '#f3f4f6', color: '#6b7280', cursor: 'pointer' }}>
                                                    Decline
                                                </button>
                                            )}
                                            {featuredNoteId === sub.id && (
                                                <button onClick={() => handleFeaturedStatusUpdate(sub.id, 'declined')} style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', border: 'none', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>
                                                    Confirm Decline
                                                </button>
                                            )}
                                            <a href={sub.image_url} download style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', textDecoration: 'none' }}>
                                                Download
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            )}

            {activeSection === 'collabs' && (
            <div className="admin-section">
                <h2>Collaboration Analytics</h2>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                    Track collab requests, acceptance, completion, and per-collab progress across the platform.
                </p>

                {collabAnalyticsError && (
                    <div className="admin-error" style={{ marginBottom: '16px' }}>{collabAnalyticsError}</div>
                )}

                {/* Filters */}
                <div className="ca-filters">
                    <div className="ca-filter-group">
                        <label className="ca-filter-label">Period</label>
                        <div className="ca-btn-group">
                            {['7d', '30d', '90d'].map(p => (
                                <button key={p} className={`ca-btn-toggle ${collabDatePreset === p ? 'active' : ''}`}
                                    onClick={() => { setCollabDatePreset(p); setCollabPage(0); }}>
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="ca-filter-group">
                        <label className="ca-filter-label">Group by</label>
                        <div className="ca-btn-group">
                            {['day', 'week', 'month'].map(g => (
                                <button key={g} className={`ca-btn-toggle ${collabGroupBy === g ? 'active' : ''}`}
                                    onClick={() => { setCollabGroupBy(g); setCollabPage(0); }}>
                                    {g}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="ca-filter-group">
                        <label className="ca-filter-label">Status</label>
                        <select className="ca-select" value={collabStatusFilter}
                            onChange={e => { setCollabStatusFilter(e.target.value); setCollabPage(0); }}>
                            <option value="all">All</option>
                            <optgroup label="Collaboration">
                                <option value="active">Active</option>
                                <option value="paused">Paused</option>
                                <option value="completed">Completed</option>
                            </optgroup>
                            <optgroup label="Request">
                                <option value="pending">Pending (Pitched)</option>
                                <option value="declined">Declined</option>
                                <option value="expired">Expired</option>
                            </optgroup>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                    <div className="ca-filter-group ca-filter-search">
                        <label className="ca-filter-label">Search</label>
                        <input type="text" className="ca-search-input" placeholder="Name or handle..."
                            value={collabSearch} onChange={e => setCollabSearch(e.target.value)} />
                    </div>
                </div>

                {loadingCollabAnalytics && !collabAnalytics ? (
                    <p>Loading collaboration analytics...</p>
                ) : collabAnalytics ? (
                    <>
                        {/* KPI Cards */}
                        <div className="ca-kpi-grid">
                            <div className="ca-kpi-card ca-kpi-blue-solid">
                                <div className="ca-kpi-value">{collabAnalytics.summary.requestsCreated}</div>
                                <div className="ca-kpi-label">Requests Sent</div>
                            </div>
                            <div className="ca-kpi-card ca-kpi-blue">
                                <div className="ca-kpi-value">{collabAnalytics.summary.requestsAccepted}</div>
                                <div className="ca-kpi-label">Accepted</div>
                                <div className="ca-kpi-sub">{collabAnalytics.summary.acceptanceRate != null ? `${collabAnalytics.summary.acceptanceRate}%` : 'N/A'} acceptance</div>
                            </div>
                            <div className="ca-kpi-card ca-kpi-blue-solid">
                                <div className="ca-kpi-value">{collabAnalytics.summary.collabsCreated}</div>
                                <div className="ca-kpi-label">Collabs Created</div>
                            </div>
                            <div className="ca-kpi-card ca-kpi-green">
                                <div className="ca-kpi-value">{collabAnalytics.summary.completedCollabs}</div>
                                <div className="ca-kpi-label">Completed</div>
                                <div className="ca-kpi-sub">{collabAnalytics.summary.completionRate != null ? `${collabAnalytics.summary.completionRate}%` : 'N/A'} completion</div>
                            </div>
                        </div>

                        {/* Secondary KPIs */}
                        <div className="ca-kpi-secondary">
                            <div className="ca-kpi-pill">
                                <span className="ca-kpi-pill-val">{collabAnalytics.summary.activeCollabs}</span> Active
                            </div>
                            <div className="ca-kpi-pill">
                                <span className="ca-kpi-pill-val">{collabAnalytics.summary.pausedCollabs}</span> Paused
                            </div>
                            <div className="ca-kpi-pill ca-kpi-pill-warn">
                                <span className="ca-kpi-pill-val">{collabAnalytics.summary.stalledActiveCollabs}</span> Stalled (14d+)
                            </div>
                            <div className="ca-kpi-pill">
                                <span className="ca-kpi-pill-val">{collabAnalytics.summary.requestsDeclined}</span> Declined
                            </div>
                            <div className="ca-kpi-pill">
                                <span className="ca-kpi-pill-val">{collabAnalytics.summary.avgTimeToAcceptDays ?? '—'}</span> Avg days to accept
                            </div>
                            <div className="ca-kpi-pill">
                                <span className="ca-kpi-pill-val">{collabAnalytics.summary.avgTimeToCompleteDays ?? '—'}</span> Avg days to complete
                            </div>
                        </div>

                        {/* Funnel */}
                        {collabAnalytics.funnel && collabAnalytics.funnel.length > 0 && (() => {
                            const maxCount = Math.max(...collabAnalytics.funnel.map(s => s.count), 1);
                            return (
                                <div className="ca-subsection">
                                    <h3>Conversion Funnel</h3>
                                    <div className="ca-funnel">
                                        {collabAnalytics.funnel.map((stage, i) => (
                                            <div key={stage.stage} className="ca-funnel-row">
                                                <div className="ca-funnel-label">{stage.stage}</div>
                                                <div className="ca-funnel-bar-wrap">
                                                    <div className="ca-funnel-bar" style={{ width: `${Math.max((stage.count / maxCount) * 100, 2)}%` }} />
                                                </div>
                                                <div className="ca-funnel-count">{stage.count}</div>
                                                {i > 0 && collabAnalytics.funnel[i - 1].count > 0 && (
                                                    <div className="ca-funnel-drop">
                                                        {((stage.count / collabAnalytics.funnel[i - 1].count) * 100).toFixed(0)}%
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Trend Chart (SVG bar chart) */}
                        {collabAnalytics.trendByPeriod && collabAnalytics.trendByPeriod.length > 0 && (() => {
                            const data = collabAnalytics.trendByPeriod;
                            const maxVal = Math.max(...data.map(d => d.totalEvents), 1);
                            const barWidth = Math.max(Math.floor(600 / data.length) - 4, 6);
                            const chartH = 120;
                            return (
                                <div className="ca-subsection">
                                    <h3>Activity Trend</h3>
                                    <div className="ca-chart-wrap">
                                        <svg width="100%" viewBox={`0 0 ${data.length * (barWidth + 4)} ${chartH + 24}`} preserveAspectRatio="none">
                                            {data.map((d, i) => {
                                                const h = (d.totalEvents / maxVal) * chartH;
                                                return (
                                                    <g key={i}>
                                                        <rect x={i * (barWidth + 4)} y={chartH - h} width={barWidth} height={h} rx={2} fill="#0f084b" opacity={0.85} />
                                                        <title>{new Date(d.period).toLocaleDateString()} — {d.totalEvents} events</title>
                                                    </g>
                                                );
                                            })}
                                        </svg>
                                        <div className="ca-chart-labels">
                                            <span>{new Date(data[0].period).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                                            <span>{new Date(data[data.length - 1].period).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Health: Check-in + Deliverables */}
                        <div className="ca-health-grid">
                            {/* Check-in distribution */}
                            <div className="ca-subsection">
                                <h3>Check-in Health</h3>
                                {Object.keys(collabAnalytics.health.checkinDistribution).length > 0 ? (() => {
                                    const dist = collabAnalytics.health.checkinDistribution;
                                    const total = Object.values(dist).reduce((a, b) => a + b, 0) || 1;
                                    const statusColors = { on_track: '#22c55e', blocked: '#ef4444', need_reschedule: '#f59e0b', need_help: '#3b82f6' };
                                    return (
                                        <div className="ca-bar-stack-wrap">
                                            <div className="ca-bar-stack">
                                                {Object.entries(dist).map(([s, c]) => (
                                                    <div key={s} className="ca-bar-seg" style={{ width: `${(c / total) * 100}%`, background: statusColors[s] || '#94a3b8' }}
                                                        title={`${s.replace(/_/g, ' ')}: ${c}`} />
                                                ))}
                                            </div>
                                            <div className="ca-bar-legend">
                                                {Object.entries(dist).map(([s, c]) => (
                                                    <span key={s} className="ca-bar-legend-item">
                                                        <span className="ca-bar-dot" style={{ background: statusColors[s] || '#94a3b8' }} />
                                                        {s.replace(/_/g, ' ')} ({c})
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })() : <p className="ca-muted">No check-ins in this period.</p>}
                            </div>

                            {/* Deliverable distribution */}
                            <div className="ca-subsection">
                                <h3>Deliverable Status</h3>
                                {Object.keys(collabAnalytics.health.deliverableDistribution).length > 0 ? (() => {
                                    const dist = collabAnalytics.health.deliverableDistribution;
                                    const total = Object.values(dist).reduce((a, b) => a + b, 0) || 1;
                                    const delivColors = { pending: '#94a3b8', submitted: '#3b82f6', approved: '#22c55e', changes_requested: '#f59e0b', cancelled: '#ef4444' };
                                    return (
                                        <div className="ca-bar-stack-wrap">
                                            <div className="ca-bar-stack">
                                                {Object.entries(dist).map(([s, c]) => (
                                                    <div key={s} className="ca-bar-seg" style={{ width: `${(c / total) * 100}%`, background: delivColors[s] || '#94a3b8' }}
                                                        title={`${s.replace(/_/g, ' ')}: ${c}`} />
                                                ))}
                                            </div>
                                            <div className="ca-bar-legend">
                                                {Object.entries(dist).map(([s, c]) => (
                                                    <span key={s} className="ca-bar-legend-item">
                                                        <span className="ca-bar-dot" style={{ background: delivColors[s] || '#94a3b8' }} />
                                                        {s.replace(/_/g, ' ')} ({c})
                                                    </span>
                                                ))}
                                            </div>
                                            {collabAnalytics.health.overdueDeliverables > 0 && (
                                                <div className="ca-overdue-badge">{collabAnalytics.health.overdueDeliverables} overdue</div>
                                            )}
                                        </div>
                                    );
                                })() : <p className="ca-muted">No deliverables in this period.</p>}
                            </div>
                        </div>

                        {/* Top Connectors */}
                        {collabAnalytics.topConnectors && collabAnalytics.topConnectors.length > 0 && (
                            <div className="ca-subsection">
                                <h3>Top Connectors</h3>
                                <div className="ca-connector-list">
                                    {collabAnalytics.topConnectors.map((tc, idx) => (
                                        <div key={tc.userId} className="ca-connector-row">
                                            <span className="ca-connector-rank">#{idx + 1}</span>
                                            <span className="ca-connector-name">{tc.displayName}</span>
                                            <span className="ca-connector-role">{tc.role}</span>
                                            <span className="ca-connector-count">{tc.collabCount} collabs</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Detailed Collab Table */}
                        <div className="ca-subsection" style={{ marginTop: '24px' }}>
                            <h3>Collaboration Details ({collabAnalytics.pagination?.total ?? 0})</h3>
                            {sortedCollabs.length > 0 ? (
                                <>
                                    <div className="ca-table-wrap">
                                        <table className="ca-table">
                                            <thead>
                                                <tr>
                                                    <th>Participants</th>
                                                    <th>Target</th>
                                                    <th>Status</th>
                                                    <th className="ca-sortable" onClick={() => handleCollabSort('progress')}>
                                                        Progress {collabSortField === 'progress' ? (collabSortDir === 'asc' ? '↑' : '↓') : ''}
                                                    </th>
                                                    <th>Deliverables</th>
                                                    <th>Check-in</th>
                                                    <th className="ca-sortable" onClick={() => handleCollabSort('lastActivityAt')}>
                                                        Last Activity {collabSortField === 'lastActivityAt' ? (collabSortDir === 'asc' ? '↑' : '↓') : ''}
                                                    </th>
                                                    <th className="ca-sortable" onClick={() => handleCollabSort('ageDays')}>
                                                        Age {collabSortField === 'ageDays' ? (collabSortDir === 'asc' ? '↑' : '↓') : ''}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedCollabs.map(c => (
                                                    <React.Fragment key={c.id}>
                                                        <tr className={`ca-row ${expandedCollabId === c.id ? 'ca-row-expanded' : ''}`}
                                                            onClick={() => setExpandedCollabId(expandedCollabId === c.id ? null : c.id)}>
                                                            <td className="ca-cell-participants">
                                                                {c.participants.map(p => (
                                                                    <div key={p.userId} className="ca-participant">
                                                                        <span className={`ca-role-dot ca-role-${p.role}`} />
                                                                        {p.handle ? `@${p.handle}` : p.firstName || 'Unknown'}
                                                                    </div>
                                                                ))}
                                                            </td>
                                                            <td>{c.targetLabel || (c.targetKind ? c.targetKind : '—')}</td>
                                                            <td><span className={`ca-status-badge ca-status-${c.lifecycleStatus || c.status}`}>{(c.lifecycleStatus || c.status).replace(/_/g, ' ')}</span></td>
                                                            <td>
                                                                <div className="ca-progress-bar-wrap">
                                                                    <div className="ca-progress-bar" style={{ width: `${c.progress.percent}%` }} />
                                                                </div>
                                                                <span className="ca-progress-text">{c.progress.percent}%</span>
                                                            </td>
                                                            <td className="ca-cell-deliv">
                                                                {c.deliverables.total > 0
                                                                    ? `${c.deliverables.approved}/${c.deliverables.total} approved`
                                                                    : '—'}
                                                            </td>
                                                            <td>
                                                                {c.latestCheckin ? (
                                                                    <span className={`ca-checkin-badge ca-checkin-${c.latestCheckin.status}`}>
                                                                        {c.latestCheckin.status.replace(/_/g, ' ')}
                                                                    </span>
                                                                ) : '—'}
                                                            </td>
                                                            <td className="ca-cell-date">
                                                                {c.lastActivityAt ? new Date(c.lastActivityAt).toLocaleDateString() : '—'}
                                                            </td>
                                                            <td>{c.ageDays}d</td>
                                                        </tr>
                                                        {expandedCollabId === c.id && (
                                                            <tr className="ca-expanded-row">
                                                                <td colSpan={8}>
                                                                    <div className="ca-expanded-content">
                                                                        <div className="ca-expanded-col">
                                                                            <h4>Agreement Progress</h4>
                                                                            <div className="ca-expanded-stat">Total: {c.progress.totalAgreements}</div>
                                                                            <div className="ca-expanded-stat">Creator confirmed: {c.progress.creatorConfirmations}</div>
                                                                            <div className="ca-expanded-stat">Host confirmed: {c.progress.hostConfirmations}</div>
                                                                            <div className="ca-expanded-stat">Locked: {c.progress.lockedItems} {c.progress.allLocked ? '(all)' : ''}</div>
                                                                        </div>
                                                                        <div className="ca-expanded-col">
                                                                            <h4>Deliverables</h4>
                                                                            <div className="ca-expanded-stat">Pending: {c.deliverables.pending}</div>
                                                                            <div className="ca-expanded-stat">Submitted: {c.deliverables.submitted}</div>
                                                                            <div className="ca-expanded-stat">Approved: {c.deliverables.approved}</div>
                                                                            <div className="ca-expanded-stat">Changes req: {c.deliverables.changesRequested}</div>
                                                                        </div>
                                                                        <div className="ca-expanded-col">
                                                                            <h4>Details</h4>
                                                                            {c.source === 'request' && <div className="ca-expanded-stat">Type: Collab Request</div>}
                                                                            <div className="ca-expanded-stat">Created: {new Date(c.createdAt).toLocaleDateString()}</div>
                                                                            {c.completedAt && <div className="ca-expanded-stat">Completed: {new Date(c.completedAt).toLocaleDateString()}</div>}
                                                                            {c.declineReason && <div className="ca-expanded-stat">Decline reason: {c.declineReason}</div>}
                                                                            <div className="ca-expanded-stat">Last event: {c.lastEventType ? c.lastEventType.replace(/^collab_/, '').replace(/_/g, ' ') : '—'}</div>
                                                                            <div className="ca-expanded-stat">ID: <code style={{ fontSize: '11px' }}>{c.id.slice(0, 8)}</code></div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {collabAnalytics.pagination && (
                                        <div className="ca-pagination">
                                            <button className="ca-btn-toggle" disabled={collabPage === 0}
                                                onClick={() => setCollabPage(p => Math.max(0, p - 1))}>
                                                Prev
                                            </button>
                                            <span className="ca-page-info">
                                                {collabPage * 25 + 1}–{Math.min((collabPage + 1) * 25, collabAnalytics.pagination.total)} of {collabAnalytics.pagination.total}
                                            </span>
                                            <button className="ca-btn-toggle" disabled={!collabAnalytics.pagination.hasMore}
                                                onClick={() => setCollabPage(p => p + 1)}>
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p className="ca-muted">No collaborations found for this period and filter.</p>
                            )}
                        </div>
                    </>
                ) : null}
            </div>
            )}

            {activeSection === 'uploads' && (
            <div className="admin-section">
                <h2>Collab Uploads</h2>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                    Photos, videos, and files creators and hosts upload to collaborations done through Postaway.
                    These are pulled live from collaboration deliverables.
                </p>

                {collabUploadsError && (
                    <div className="admin-error" style={{ marginBottom: '16px' }}>{collabUploadsError}</div>
                )}

                <div className="cu-toolbar">
                    <div className="cu-btn-group">
                        {[
                            { key: 'file', label: `Uploaded files (${collabUploadsSummary.files})` },
                            { key: 'link', label: `Links (${collabUploadsSummary.links})` },
                            { key: 'all', label: 'All' },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                type="button"
                                className={`cu-btn-toggle ${collabUploadsKind === tab.key ? 'active' : ''}`}
                                onClick={() => setCollabUploadsKind(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <input
                        type="text"
                        className="cu-search-input"
                        placeholder="Search by title, name, or handle..."
                        value={collabUploadsSearch}
                        onChange={(e) => setCollabUploadsSearch(e.target.value)}
                    />
                </div>

                {loadingCollabUploads && collabUploads.length === 0 ? (
                    <p className="cu-muted">Loading uploads...</p>
                ) : collabUploads.length === 0 ? (
                    <p className="cu-muted">No uploads found for this filter.</p>
                ) : (
                    <div className="cu-grid">
                        {collabUploads.map((item) => {
                            const isImage = item.mediaType === 'image';
                            const isVideo = item.mediaType === 'video';
                            const previewSrc = isImage
                                ? (getCloudinaryUrl(item.url, { width: 480, height: 360, crop: 'fill', quality: 'auto', format: 'auto' }) || item.url)
                                : null;
                            const uploaderLabel = item.uploader.handle
                                ? `@${item.uploader.handle}`
                                : (item.uploader.displayName || 'Unknown');
                            const partners = (item.collaboration.participants || [])
                                .map(p => p.handle ? `@${p.handle}` : p.displayName)
                                .join(' × ');
                            return (
                                <div key={item.id} className="cu-card">
                                    <div className="cu-thumb">
                                        {isImage ? (
                                            <a href={item.url} target="_blank" rel="noopener noreferrer">
                                                <img src={previewSrc} alt={item.title || 'Upload'} loading="lazy" />
                                            </a>
                                        ) : (
                                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="cu-thumb-placeholder">
                                                {isVideo ? <PlayCircle size={40} /> : (item.kind === 'link' ? <ExternalLink size={40} /> : <FileText size={40} />)}
                                                <span>{isVideo ? 'Video' : (item.kind === 'link' ? 'External link' : 'File')}</span>
                                            </a>
                                        )}
                                        <span className={`cu-status cu-status-${item.status}`}>{(item.status || '').replace(/_/g, ' ')}</span>
                                    </div>
                                    <div className="cu-meta">
                                        <div className="cu-title" title={item.title}>{item.title || 'Untitled'}</div>
                                        <div className="cu-sub">
                                            <span className={`cu-role cu-role-${item.uploader.role || 'other'}`} />
                                            {uploaderLabel}
                                            {item.uploader.role ? ` · ${item.uploader.role}` : ''}
                                        </div>
                                        {partners && <div className="cu-partners">{partners}</div>}
                                        <div className="cu-date">{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}</div>
                                        <div className="cu-actions">
                                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="cu-action">
                                                <ExternalLink size={14} /> Open
                                            </a>
                                            {item.kind === 'file' && (
                                                <a href={item.url} download className="cu-action">
                                                    <Download size={14} /> Download
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            )}

            {activeSection === 'reviewCards' && (
            <div className="admin-section">
                <h2>Review Cards</h2>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                    Every pending and accepted review as its shareable card. Open a card to add or
                    replace the collab photo and download the graphic — perfect for retroactively
                    creating share graphics. Nothing here sends notifications to users.
                </p>

                {reviewCardsError && (
                    <div className="admin-error" style={{ marginBottom: '16px' }}>{reviewCardsError}</div>
                )}

                <div className="cu-toolbar">
                    <div className="cu-btn-group">
                        {[
                            { key: 'all', label: `All (${reviewCardsSummary.total})` },
                            { key: 'pending', label: `Pending (${reviewCardsSummary.pending})` },
                            { key: 'accepted', label: `Accepted (${reviewCardsSummary.accepted})` },
                            { key: 'noPhoto', label: `Needs photo (${reviewCardsSummary.missingPhoto})` },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                type="button"
                                className={`cu-btn-toggle ${reviewCardsFilter === tab.key ? 'active' : ''}`}
                                onClick={() => setReviewCardsFilter(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <input
                        type="text"
                        className="cu-search-input"
                        placeholder="Search by handle, name, or review text..."
                        value={reviewCardsSearch}
                        onChange={(e) => setReviewCardsSearch(e.target.value)}
                    />
                </div>

                {(() => {
                    const q = reviewCardsSearch.trim().toLowerCase();
                    const filtered = reviewCards.filter(r => {
                        if (reviewCardsFilter === 'pending' && r.isAccepted) return false;
                        if (reviewCardsFilter === 'accepted' && !r.isAccepted) return false;
                        if (reviewCardsFilter === 'noPhoto' && r.heroImageUrl) return false;
                        if (!q) return true;
                        return [r.subjectName, r.subjectHandle, r.reviewerName, r.reviewerHandle, r.reviewText, r.propertyName]
                            .some(v => String(v || '').toLowerCase().includes(q));
                    });

                    if (loadingReviewCards && reviewCards.length === 0) {
                        return <p className="cu-muted">Loading reviews...</p>;
                    }
                    if (filtered.length === 0) {
                        return <p className="cu-muted">No reviews match this filter.</p>;
                    }
                    return (
                        <div className="rc-grid">
                            {filtered.map((rev) => (
                                <div key={`${rev.reviewType}-${rev.id}`} className="rc-card">
                                    <div className="rc-preview">
                                        <ReviewShareCard
                                            backgroundImageUrl={rev.heroImageUrl}
                                            circleImageUrl={rev.circleImageUrl}
                                            subjectAvatarUrl={rev.subjectAvatar}
                                            reviewerAvatarUrl={rev.reviewerAvatar}
                                            reviewText={rev.reviewText}
                                            rating={rev.rating}
                                            subjectName={rev.subjectName}
                                            subjectHandle={rev.subjectHandle}
                                            reviewerHandle={rev.reviewerHandle}
                                            reviewType={rev.reviewType}
                                        />
                                    </div>
                                    <div className="rc-meta">
                                        <div className="rc-badges">
                                            <span className={`rc-badge ${rev.isAccepted ? 'rc-badge-accepted' : 'rc-badge-pending'}`}>
                                                {rev.isAccepted ? 'Accepted' : 'Pending'}
                                            </span>
                                            <span className="rc-badge rc-badge-type">
                                                {rev.reviewType === 'creator' ? 'Creator review' : 'Host review'}
                                            </span>
                                            {!rev.heroImageUrl && (
                                                <span className="rc-badge rc-badge-nophoto">No photo</span>
                                            )}
                                        </div>
                                        <div className="rc-handles">
                                            {(rev.subjectHandle ? `@${rev.subjectHandle}` : rev.subjectName)}
                                            {' × '}
                                            {(rev.reviewerHandle ? `@${rev.reviewerHandle}` : rev.reviewerName || '?')}
                                        </div>
                                        <div className="rc-date">
                                            {rev.createdAt ? new Date(rev.createdAt).toLocaleDateString() : ''}
                                            {rev.propertyName ? ` · ${rev.propertyName}` : ''}
                                        </div>
                                        <button
                                            type="button"
                                            className="rc-open-btn"
                                            onClick={() => setShareCardReview({
                                                id: rev.id,
                                                reviewType: rev.reviewType,
                                                backgroundImageUrl: rev.heroImageUrl,
                                                circleImageUrl: rev.circleImageUrl,
                                                subjectAvatarUrl: rev.subjectAvatar,
                                                reviewerAvatarUrl: rev.reviewerAvatar,
                                                reviewText: rev.reviewText,
                                                rating: rev.rating,
                                                subjectName: rev.subjectName,
                                                subjectHandle: rev.subjectHandle,
                                                reviewerHandle: rev.reviewerHandle,
                                            })}
                                        >
                                            <ImagePlus size={14} />
                                            {rev.heroImageUrl ? 'Open / change photo' : 'Open / add photo'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                })()}

                <ReviewShareModal
                    isOpen={!!shareCardReview}
                    onClose={() => setShareCardReview(null)}
                    review={shareCardReview}
                    canEdit
                    adminPassword="Unicorn2025!"
                    onImagesUpdated={handleAdminHeroUpdated}
                />
            </div>
            )}
            </main>
        </div>
    );
};

export default Admin;
